import {
  Scene,
  Engine,
  Vector3,
  Color3,
  FreeCamera,
  HemisphericLight,
  MeshBuilder,
  StandardMaterial,
  Mesh,
  KeyboardEventTypes,
  InstancedMesh,
  Animation as BJSAnimation,
  Color4,
  PointerEventTypes,
  DynamicTexture,
  Ray
} from 'babylonjs';
import { connectToServer, setGameObjectFactory } from './sync';
import { BUILDING_DEFINITIONS, BuildingType, UnitType, UnitStance, UNIT_DEFINITIONS, WeaponStatsMap, SHOW_ALL_ORE } from '../shared/MessageType';
import { FogOfWarSystem } from './fogofwar';
import { UnitBuildingInfoPanel3D } from './unitBuildingInfoPanel3D';
import { Unit3D, Building3D, Ore3D } from './interfaces3d';
import { GameObjectFactory3D } from './GameObjectFactory';
import { createLowPolyTerrain } from './worldTerrain';
import { TutorialSystem } from './tutorial';
import { SHOW_MOVEMENT_PILLARS } from './constants';

interface WorldCallbacks {
  callback: (id: number, socket: any) => void;
  tickCallback: (ticks: any) => void;
  terrainCallback: (terrain: any) => void;
  oresCallback: (ores: any) => void;
  resourcesCallback: (resources: any) => void;
  dataCallback: (data: any) => void;
  queueRender: (messageType: string, payload: any) => void;
  renderSparks: (sourcePlayerId: number, source: { x: number, y: number }, target: { x: number, y: number }) => void;
  renderAttack: (playerId: number, sourceUnit: any, attackedUnit: any) => void;
  processSyncQueue: () => void;
  queueConsoleMessage?: (message: string, color?: string) => void;
}

/**
 * Transforms world coordinates (x, y) to sector A1's local 3D coordinate system
 * @param worldX World X coordinate (0-10000)
 * @param worldY World Y coordinate (0-10000)
 * @param heightOffset Height offset from sector surface (default: 0)
 * @returns Vector3 position in sector A1's coordinate system
 */
function worldToSectorA1(
  worldX: number,
  worldY: number,
  sectorA1Center: Vector3,
  sectorA1Right: Vector3,
  sectorA1Up: Vector3,
  sectorA1Normal: Vector3,
  coordinateScale: number,
  worldSize: number,
  heightOffset: number = 0
): Vector3 {
  const centeredX = (worldX - worldSize / 2) / coordinateScale;
  const centeredY = (worldY - worldSize / 2) / coordinateScale;

  // Transform to sector A1's local coordinate system
  // worldX maps to sector's right (local X), worldY maps to sector's up (local Z)
  const localOffset = sectorA1Right.scale(centeredX)
    .add(sectorA1Up.scale(centeredY))
    .add(sectorA1Normal.scale(heightOffset));

  return sectorA1Center.add(localOffset);
}

class Game3D implements WorldCallbacks {
  private canvas: HTMLCanvasElement;
  private engine: Engine;
  private scene: Scene;
  private socket: any = null;
  private worldPlayerId: number = -1;
  private gameState: any | null = null;
  private readonly WORLD_SIZE = 10000;
  private readonly COORDINATE_SCALE = 50;
  private readonly GAME_FPS = 60;
  private readonly MAX_SPARKS = 2000;

  private debugLines: { [id: string]: Mesh } = {};

  private quadtreeMeshes: {
    units: Mesh[];
    buildings: Mesh[];
    ores: Mesh[];
  } = {
      units: [],
      buildings: [],
      ores: []
    };
  private quadtreeUpdateInterval: number | null = null;

  private serverUnits: { [playerId: number]: Unit3D[] } = {};
  private serverBuildings: { [playerId: number]: Building3D[] } = {};
  private otherPlayerRaces: { [playerId: number]: string } = {};
  private otherPlayerColours: { [playerId: number]: string } = {};
  private ores: Ore3D[] = [];

  // Cache for planet surface positions keyed by rounded world coordinates + height offset
  // Key format: "x_y_heightOffset" where x and y are rounded to nearest 5 units
  private surfacePositionCache: Map<string, {
    position: Vector3,
    normal: Vector3
  }> = new Map();
  private readonly SURFACE_CACHE_GRID_SIZE = 5; // Round coordinates to this grid size for caching
  private sparks: InstancedMesh[] = [];
  private rockets: Array<{ mesh: InstancedMesh, velocity: number, lifetime: number }> = [];
  private laserBeams: Array<{ mesh: Mesh, lifetime: number }> = [];
  private missiles: Array<{ mesh: InstancedMesh, velocity: Vector3, lifetime: number, targetX: number, targetY: number, targetZ: number }> = [];
  private orbitalMassDriverLastLaunch: { [key: string]: number } = {};
  private camera: FreeCamera;
  private fogOfWar: FogOfWarSystem | null = null;
  private tutorialSystem: TutorialSystem | null = null;

  private cameraMode: 'free' | 'orbit' | 'rts' = 'free';

  private orbitAngle: number = 0;
  private orbitElevation: number = Math.PI / 4;
  private orbitRadius: number = 550;

  private orbitAngleVelocity: number = 0;
  private orbitElevationVelocity: number = 0;
  private orbitRadiusVelocity: number = 0;

  private cameraOffsetPitch: number = 0;
  private cameraOffsetYaw: number = 0;
  private cameraOffsetPitchVelocity: number = 0;
  private cameraOffsetYawVelocity: number = 0;

  private rtsCameraPosition: Vector3 = new Vector3(0, 0, 0);
  private rtsCameraHeight: number = 300;
  private rtsCameraPanVelocity: Vector3 = new Vector3(0, 0, 0);
  private rtsCameraZoomVelocity: number = 0;
  private rtsCameraRotation: number = 0;

  private isMouseDown: boolean = false;
  private isRightMouseDown: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private mouseDownX: number = 0;
  private mouseDownY: number = 0;
  private hasMouseMoved: boolean = false;

  private activeTouches: Map<number, { x: number, y: number }> = new Map();
  private lastTouchDistance: number = 0;
  private isPinching: boolean = false;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private hasTouchMoved: boolean = false;
  private wasSingleTouch: boolean = false;

  private savedFreeCameraPosition: Vector3 | null = null;
  private savedFreeCameraRotation: Vector3 | null = null;

  private isInSpaceLayer: boolean = false;
  private groundCameraPosition: Vector3 | null = null;
  private groundCameraRotation: Vector3 | null = null;
  private spaceShips: Array<{
    mesh: Mesh;
    angle: number;
    radius: number;
    orbitalSpeed: number;
    centerX: number;
    centerY: number;
    centerZ: number;
  }> = [];

  private showVisionRange: boolean = false;
  private showAttackRange: boolean = false;
  private showStanceIndicators: boolean = false;
  private showHealthbars: boolean = false;
  private showQuadtreeUnits: boolean = false;
  private showQuadtreeBuildings: boolean = false;
  private showQuadtreeOres: boolean = false;
  private visionRangeOverlays: { [key: string]: Mesh } = {};
  private attackRangeOverlays: { [key: string]: Mesh } = {};
  private stanceIndicators: { [key: string]: Mesh } = {};
  private guardPositionLines: { [key: string]: Mesh } = {};
  private healthbars: { [key: string]: { mesh: Mesh, texture: DynamicTexture } } = {};
  private sectorLabels: { [key: string]: { mesh: Mesh, texture: DynamicTexture } } = {};

  private sectorA1Center: Vector3 | null = null;
  private sectorA1Normal: Vector3 | null = null;
  private sectorA1Right: Vector3 | null = null;
  private sectorA1Up: Vector3 | null = null;
  private terrainMesh: Mesh | null = null;

  private keyState = {
    w: false,
    s: false,
    a: false,
    d: false,
    x: false,
    c: false
  };

  private baseMeshes: {
    targetBeacon: Mesh;
    positionBeacon: Mesh;
    worker: Mesh[];
    tank: Mesh[];
    tankTurret: Mesh[];
    tankBarrel: Mesh[];
    artillery: Mesh[];
    artilleryBarrel: Mesh[];
    crawler: Mesh[];
    scoutPlane: Mesh[];
    hub: Mesh[];
    house: Mesh;
    cannon: Mesh;
    cannonBarrel: Mesh;
    laserCannon: Mesh;
    laserCannonBarrel: Mesh;
    missileCannon: Mesh;
    missileCannonBarrel: Mesh;
    oreProcessor: Mesh;
    ore: Mesh;
    wall: Mesh;
    solarFarm: Mesh;
    solarFarmPanel: Mesh;
    unitEconomy: Mesh;
    unitMilitary: Mesh;
    building: Mesh;
    spark: Mesh;
    sparkAttack: Mesh;
    rocket: Mesh;
    laserBeam: Mesh;
    missile: Mesh;
  } = {} as any;

  private materials: {
    targetBeacon: StandardMaterial;
    positionBeacon: StandardMaterial;
    unitEconomy: StandardMaterial;
    unitMilitary: StandardMaterial;
    house: StandardMaterial;
    cannon: StandardMaterial;
    cannonBarrel: StandardMaterial;
    laserCannon: StandardMaterial;
    laserCannonBarrel: StandardMaterial;
    missileCannon: StandardMaterial;
    missileCannonBarrel: StandardMaterial;
    oreProcessor: StandardMaterial;
    ore: StandardMaterial;
    wall: StandardMaterial;
    solarFarm: StandardMaterial;
    solarFarmPanel: StandardMaterial;
    building: StandardMaterial;
    spark: StandardMaterial;
    sparkAttack: StandardMaterial;
    rocket: StandardMaterial;
    laserBeam: StandardMaterial;
    missile: StandardMaterial;
  } = {} as any;

  private animations: {
    oreAnimation: BJSAnimation;
  } = {} as any;

  private gameObjectFactory: GameObjectFactory3D | null = null;

  private readonly CAMERA_POSITION_KEY = 'overwhelmed-camera-position';
  private readonly CAMERA_POSITION_EPSILON = 0.1;
  private readonly CAMERA_SAVE_THROTTLE_MS = 5000;
  private lastSavedCameraPosition: Vector3 | null = null;
  private cameraSaveTimeout: number | null = null;

  private infoPanel: UnitBuildingInfoPanel3D | null = null;
  private selectedUnit: Unit3D | null = null;
  private selectedBuilding: Building3D | null = null;
  private star: Mesh;
  private stars: Mesh[] = [];
  private backgroundStars: Mesh[] = [];
  private backgroundStarDirections: Vector3[] = [];
  private satellites: Mesh[] = [];

  private consoleMessageQueue: Array<{ message: string; color?: string }> = [];
  private consoleNeedsScroll: boolean = false;
  private planet: Mesh;
  private planetRadius: number = 512; // Default radius, will be set when planet is created

  constructor() {
    this.canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
    if (!this.canvas) throw new Error("Canvas element not found!");

    this.canvas.addEventListener('contextmenu', (e) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        e.preventDefault();
      }
    });

    this.canvas.addEventListener('wheel', (e) => {
      if (this.cameraMode === 'orbit') {
        e.preventDefault();
        const wheelDelta = e.deltaY;
        const zoomSpeed = 1.0;
        const zoomAcceleration = wheelDelta > 0 ? zoomSpeed : -zoomSpeed;
        this.orbitRadiusVelocity += zoomAcceleration;
      } else if (this.cameraMode === 'rts') {
        e.preventDefault();
        const wheelDelta = e.deltaY;
        const zoomSpeed = 1.0;
        const zoomAcceleration = wheelDelta > 0 ? zoomSpeed : -zoomSpeed;
        this.rtsCameraZoomVelocity += zoomAcceleration;
      }
    }, { passive: false });

    this.canvas.addEventListener('touchstart', (e) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        e.preventDefault();
        const touches = Array.from(e.touches);

        touches.forEach(touch => {
          const rect = this.canvas.getBoundingClientRect();
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          this.activeTouches.set(touch.identifier, { x, y });
        });

        if (touches.length === 1) {
          const touch = touches[0];
          const rect = this.canvas.getBoundingClientRect();
          this.touchStartX = touch.clientX - rect.left;
          this.touchStartY = touch.clientY - rect.top;
          this.lastMouseX = this.touchStartX;
          this.lastMouseY = this.touchStartY;
          this.hasTouchMoved = false;
          this.wasSingleTouch = true;
        }
        else if (touches.length === 2) {
          const touch1 = touches[0];
          const touch2 = touches[1];
          const rect = this.canvas.getBoundingClientRect();
          const x1 = touch1.clientX - rect.left;
          const y1 = touch1.clientY - rect.top;
          const x2 = touch2.clientX - rect.left;
          const y2 = touch2.clientY - rect.top;
          const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
          this.lastTouchDistance = distance;
          this.isPinching = true;
          this.wasSingleTouch = false;
        }
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        e.preventDefault();
        const touches = Array.from(e.touches);
        const rect = this.canvas.getBoundingClientRect();

        touches.forEach(touch => {
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;
          this.activeTouches.set(touch.identifier, { x, y });
        });

        if (touches.length === 1 && !this.isPinching) {
          const touch = touches[0];
          const currentX = touch.clientX - rect.left;
          const currentY = touch.clientY - rect.top;
          const deltaX = currentX - this.lastMouseX;
          const deltaY = currentY - this.lastMouseY;

          const moveThreshold = 2;
          if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            this.hasTouchMoved = true;
          }

          if (this.hasTouchMoved) {
            if (this.cameraMode === 'orbit') {
              const touchSensitivity = 0.0001;
              this.orbitAngleVelocity += -deltaX * touchSensitivity;
              this.orbitElevationVelocity += -deltaY * touchSensitivity;
            } else if (this.cameraMode === 'rts') {
              if (this.sectorA1Right && this.sectorA1Up) {
                const panSensitivity = 0.2;
                this.rtsCameraPanVelocity.addInPlace(this.sectorA1Right.scale(-deltaX * panSensitivity));
                this.rtsCameraPanVelocity.addInPlace(this.sectorA1Up.scale(deltaY * panSensitivity));
              }
            }
          }

          this.lastMouseX = currentX;
          this.lastMouseY = currentY;
        } else if (touches.length === 2) {
          const touch1 = touches[0];
          const touch2 = touches[1];
          const x1 = touch1.clientX - rect.left;
          const y1 = touch1.clientY - rect.top;
          const x2 = touch2.clientX - rect.left;
          const y2 = touch2.clientY - rect.top;
          const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

          if (this.lastTouchDistance > 0) {
            const deltaDistance = distance - this.lastTouchDistance;
            if (this.cameraMode === 'orbit') {
              const zoomSpeed = 0.5;
              const zoomAcceleration = deltaDistance > 0 ? -zoomSpeed : zoomSpeed;
              this.orbitRadiusVelocity += zoomAcceleration;
            } else if (this.cameraMode === 'rts') {
              const zoomSpeed = 1.0;
              const zoomAcceleration = deltaDistance > 0 ? -zoomSpeed : zoomSpeed;
              this.rtsCameraZoomVelocity += zoomAcceleration;
            }
          }

          this.lastTouchDistance = distance;
        }
      }
    }, { passive: false });

    this.canvas.addEventListener('touchend', (e) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        e.preventDefault();
        const touches = Array.from(e.touches);

        const endedIdentifiers = Array.from(this.activeTouches.keys()).filter(id =>
          !touches.some(t => t.identifier === id)
        );
        endedIdentifiers.forEach(id => this.activeTouches.delete(id));

        if (touches.length < 2) {
          this.isPinching = false;
          this.lastTouchDistance = 0;
        }

        if (touches.length === 0 && this.wasSingleTouch) {
          const moveThreshold = 5;
          const totalMove = Math.sqrt(
            Math.pow(this.lastMouseX - this.touchStartX, 2) +
            Math.pow(this.lastMouseY - this.touchStartY, 2)
          );

          if (!this.hasTouchMoved || totalMove < moveThreshold) {
            const pickResult = this.scene.pick(this.touchStartX, this.touchStartY);
            this.processClick(pickResult);
          }

          this.wasSingleTouch = false;
        }
      }
    }, { passive: false });

    this.canvas.addEventListener('touchcancel', (e) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        e.preventDefault();
        this.activeTouches.clear();
        this.isPinching = false;
        this.lastTouchDistance = 0;
        this.wasSingleTouch = false;
      }
    }, { passive: false });

    this.engine = new Engine(this.canvas, true);
    this.scene = this.createScene();

    this.scene.clearColor = new Color4(0.1, 0.1, 0.1, 1);

    this.engine.runRenderLoop(() => {
      this.scene.render();
      this.updateUnitPositions();
      this.updateCameraPosition();
      this.updateSparks();
      this.updateRockets();
      this.checkAndLaunchRockets();
      this.updateLaserBeams();
      this.updateMissiles();
      this.updateRangeOverlays();
      this.updateHealthbars();
      this.updateSelectedInstancePanel();
      this.processSyncQueue();
      this.updateRespawnButtonState();
      this.updateSpaceShips();
      this.updateSatellites();
      this.updateConsole();
      this.updateStars();

      if (this.fogOfWar && this.worldPlayerId >= 0) {
        const myUnits = this.serverUnits[this.worldPlayerId] || [];
        const myBuildings = this.serverBuildings[this.worldPlayerId] || [];

        this.fogOfWar.updateVisibility(myUnits, myBuildings);

        Object.keys(this.serverUnits).forEach(playerId => {
          if (Number(playerId) !== this.worldPlayerId) {
            const enemyUnits = this.serverUnits[Number(playerId)] || [];
            const enemyBuildings = this.serverBuildings[Number(playerId)] || [];
            this.fogOfWar.updateEnemyVisibility(enemyUnits, enemyBuildings);
          }
        });
      }
    });

    window.addEventListener("resize", () => {
      this.engine.resize();
    });

    this.createAnimations();

    this.createBaseMeshes();

    this.createMaterials();

    this.fogOfWar = new FogOfWarSystem(
      this.scene,
      this.WORLD_SIZE,
      this.COORDINATE_SCALE
    );

    this.startQuadtreeVisualization();

    const that = this;

    const playerId = this.worldPlayerId;

    this.gameObjectFactory = {
      addExisting: (obj: any) => {
      },
      getWorker: (world: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 1, x, y, playerId, id });
      },
      getTank: (world: any, k: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 2, x, y, playerId, id });
      },
      getCrawler: (world: any, x: any, y: any, fillColor: number, fillAlpha: number, playerId: number) => {
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 5, x, y, playerId, id });
      },
      getArtillery: (world: any, x: any, y: any, playerId: number) => {
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 9, x, y, playerId, id });
      },
      getScoutPlane: (world: any, x: any, y: any, playerId: number) => {
        let id = Math.floor(Math.random() * 1000000) + 20000;
        return that.handleUnitSpawn({ k: 10, x, y, playerId, id });
      },
      getHub: (world: any, x: any, y: any) => {
        // Use own player index (0) for preview
        const buildingInstance = this.baseMeshes.hub[0].createInstance("hub");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Hub,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        return building;
      },
      getHouse: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.house.createInstance("house");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.House,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        return building;
      },
      getCannon: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.cannon.createInstance("cannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Cannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);

        const cannonBarrel = this.baseMeshes.cannonBarrel.createInstance("cannonBarrel");
        building.mesh.addChild(cannonBarrel);
        (building as any).cannonBarrel = cannonBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        // Position barrel so one end is at turret center, other end extends forward
        const barrelLength = 0.5; // Half the barrel length
        // It's sin for x and cos for z I guess
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        cannonBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        cannonBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getLaserCannon: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.laserCannon.createInstance("laserCannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.LaserCannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        const laserBarrel = this.baseMeshes.laserCannonBarrel.createInstance("laserCannonBarrel");
        building.mesh.addChild(laserBarrel);
        (building as any).cannonBarrel = laserBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        // Position barrel so one end is at turret center, other end extends forward
        const barrelLength = 0.5; // Half the barrel length
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        laserBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        laserBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getMissileCannon: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.missileCannon.createInstance("missileCannon");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.MissileCannon,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        const missileBarrel = this.baseMeshes.missileCannonBarrel.createInstance("missileCannonBarrel");
        building.mesh.addChild(missileBarrel);
        (building as any).cannonBarrel = missileBarrel;
        (building as any).angle = Math.random() * 2 * Math.PI;
        const barrelLength = 0.5;
        const offsetX = Math.sin((building as any).angle) * barrelLength;
        const offsetZ = Math.cos((building as any).angle) * barrelLength;
        missileBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
        missileBarrel.rotation = new Vector3(0, (building as any).angle + Math.PI / 2, 0);
        return building;
      },
      getOreProcessor: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.oreProcessor.createInstance("oreProcessor");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.OreProcessor,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        return building;
      },
      getWall: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.wall.createInstance("wall");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.Wall,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        return building;
      },
      getSolarFarm: (world: any, x: any, y: any) => {
        const buildingInstance = this.baseMeshes.solarFarm.createInstance("solarFarm");
        const building = {
          mesh: buildingInstance,
          id: 0,
          playerId: 0,
          k: BuildingType.SolarFarm,
          x,
          y,
          destroy: () => {
            buildingInstance.dispose();
          }
        } as Building3D;
        this.positionAndAlignToPlanetSurface(building.mesh, x, y, 1);
        const solarFarmPanel = this.baseMeshes.solarFarmPanel.createInstance("solarFarmPanel");
        building.mesh.addChild(solarFarmPanel);
        (building as any).solarFarmPanel = solarFarmPanel;
        solarFarmPanel.position = new Vector3(0, 0.5, 0);
        solarFarmPanel.rotation = new Vector3(0, 0, 0);

        solarFarmPanel.lookAt(this.star.position);
        solarFarmPanel.rotation.y += Math.PI;

        return building;
      },
    };

    setGameObjectFactory(this.gameObjectFactory);

    connectToServer(this);

    const file = document.getElementById('file') as HTMLInputElement;

    file.onchange = () => {
      const fileContent = file.files[0];
      if (fileContent) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const code = e.target?.result as string;
          this.saveCodeToServer(code);
        };
        reader.readAsText(fileContent);
      }
    };


    const loggedInAs = document.getElementById('hud-logged-in-as-wrapper');
    if (loggedInAs) {
      loggedInAs.style.display = 'none';
    }
    const loginButton = document.getElementById('login');
    if (loginButton) {
      loginButton.style.display = 'none';
    }
    const logoutButton = document.getElementById('logout');
    if (logoutButton) {
      logoutButton.style.display = 'none';
    }

    const codeButton = document.getElementById('run');
    if (codeButton) {
      codeButton.onclick = () => this.saveCode();

      codeButton.style.display = 'none';
    }

    const respawnButton = document.getElementById('respawn');
    if (respawnButton) {
      respawnButton.onclick = () => this.handleRespawn();
      respawnButton.style.display = 'none';
    }

    const clearConsoleButton = document.getElementById('clearconsole');
    if (clearConsoleButton) {
      clearConsoleButton.onclick = () => {
        const console = document.getElementById('console');
        if (console) {
          console.innerHTML = '';
        }
      }
    }

    const toggleVisionRangeButton = document.getElementById('toggle-vision-range');
    if (toggleVisionRangeButton) {
      toggleVisionRangeButton.onclick = () => {
        this.showVisionRange = !this.showVisionRange;
        this.updateRangeOverlays();
        toggleVisionRangeButton.style.backgroundColor = this.showVisionRange ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleAttackRangeButton = document.getElementById('toggle-attack-range');
    if (toggleAttackRangeButton) {
      toggleAttackRangeButton.onclick = () => {
        this.showAttackRange = !this.showAttackRange;
        this.updateRangeOverlays();
        toggleAttackRangeButton.style.backgroundColor = this.showAttackRange ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleStanceIndicatorsButton = document.getElementById('toggle-stance-indicators');
    if (toggleStanceIndicatorsButton) {
      toggleStanceIndicatorsButton.onclick = () => {
        this.showStanceIndicators = !this.showStanceIndicators;
        this.updateRangeOverlays();
        toggleStanceIndicatorsButton.style.backgroundColor = this.showStanceIndicators ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleHealthbarsButton = document.getElementById('toggle-healthbars');
    if (toggleHealthbarsButton) {
      toggleHealthbarsButton.onclick = () => {
        this.showHealthbars = !this.showHealthbars;
        this.updateHealthbars();
        toggleHealthbarsButton.style.backgroundColor = this.showHealthbars ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleQuadtreeUnitsButton = document.getElementById('toggle-quadtree-units');
    if (toggleQuadtreeUnitsButton) {
      toggleQuadtreeUnitsButton.onclick = () => {
        this.showQuadtreeUnits = !this.showQuadtreeUnits;
        this.updateQuadtreeVisualization();
        toggleQuadtreeUnitsButton.style.backgroundColor = this.showQuadtreeUnits ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleQuadtreeBuildingsButton = document.getElementById('toggle-quadtree-buildings');
    if (toggleQuadtreeBuildingsButton) {
      toggleQuadtreeBuildingsButton.onclick = () => {
        this.showQuadtreeBuildings = !this.showQuadtreeBuildings;
        this.updateQuadtreeVisualization();
        toggleQuadtreeBuildingsButton.style.backgroundColor = this.showQuadtreeBuildings ? '#0891b2' : '#f0f0f0';
      };
    }

    const toggleQuadtreeOresButton = document.getElementById('toggle-quadtree-ores');
    if (toggleQuadtreeOresButton) {
      toggleQuadtreeOresButton.onclick = () => {
        this.showQuadtreeOres = !this.showQuadtreeOres;
        this.updateQuadtreeVisualization();
        toggleQuadtreeOresButton.style.backgroundColor = this.showQuadtreeOres ? '#0891b2' : '#f0f0f0';
      };
    }

    const cameraModeButtons = document.querySelectorAll('.camera-mode-btn');
    cameraModeButtons.forEach(button => {
      button.addEventListener('click', () => {
        const mode = button.getAttribute('data-mode') as 'free' | 'orbit' | 'rts';
        this.setCameraMode(mode);
        this.updateCameraModeButtons();
        const container = button.closest('.ui-menu-container');
        if (container) {
          container.classList.remove('expanded');
          const toggleButton = container.querySelector('#toggle-camera-menu');
          if (toggleButton) {
            const span = toggleButton.querySelector('span');
            if (span) {
              span.textContent = 'Camera ▼';
            }
          }
        }
      });
    });

    this.updateCameraModeButtons();

    const savedCode = localStorage.getItem('overwhelmed-code');
    if (savedCode) {
      const aceEditor = (window as any).editor;
      if (aceEditor) {
        aceEditor.setValue(savedCode);
        this.saveCode();
      }
    }

    this.loadCameraPosition();

    this.infoPanel = new UnitBuildingInfoPanel3D();

    this.tutorialSystem = new TutorialSystem();

    this.setupClickDetection();
  }

  private setupClickDetection(): void {
    this.scene.onPointerObservable.add((pointerInfo) => {
      if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
        if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
          if (pointerInfo.event.button === 0) {
            this.isMouseDown = true;
            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
            this.mouseDownX = pointerInfo.event.offsetX;
            this.mouseDownY = pointerInfo.event.offsetY;
            this.hasMouseMoved = false;
          }
          else if (pointerInfo.event.button === 2 && this.cameraMode === 'orbit') {
            this.isRightMouseDown = true;
            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }
        } else if (pointerInfo.type === PointerEventTypes.POINTERUP) {
          if (pointerInfo.event.button === 0) {
            const moveThreshold = 5;
            const totalMove = Math.sqrt(
              Math.pow(pointerInfo.event.offsetX - this.mouseDownX, 2) +
              Math.pow(pointerInfo.event.offsetY - this.mouseDownY, 2)
            );
            const wasClick = totalMove < moveThreshold;

            this.isMouseDown = false;

            if (wasClick) {
            } else {
              return;
            }
          } else if (pointerInfo.event.button === 2) {
            this.isRightMouseDown = false;
          }
        } else if (pointerInfo.type === PointerEventTypes.POINTERMOVE) {
          if (this.isMouseDown) {
            const deltaX = pointerInfo.event.offsetX - this.lastMouseX;
            const deltaY = pointerInfo.event.offsetY - this.lastMouseY;

            const moveThreshold = 2;
            if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
              this.hasMouseMoved = true;
            }

            if (this.hasMouseMoved) {
              if (this.cameraMode === 'orbit') {
                const mouseSensitivity = 0.0001;
                this.orbitAngleVelocity += -deltaX * mouseSensitivity;
                this.orbitElevationVelocity += -deltaY * mouseSensitivity;
              } else if (this.cameraMode === 'rts') {
                if (this.sectorA1Right && this.sectorA1Up) {
                  const panSensitivity = 0.2;
                  this.rtsCameraPanVelocity.addInPlace(this.sectorA1Right.scale(-deltaY * panSensitivity));
                  this.rtsCameraPanVelocity.addInPlace(this.sectorA1Up.scale(-deltaX * panSensitivity));
                }
              }
            }

            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }

          if (this.isRightMouseDown && this.cameraMode === 'orbit') {
            const deltaX = pointerInfo.event.offsetX - this.lastMouseX;
            const deltaY = pointerInfo.event.offsetY - this.lastMouseY;

            const offsetSensitivity = 0.0005;
            this.cameraOffsetYawVelocity += deltaX * offsetSensitivity;
            this.cameraOffsetPitchVelocity -= deltaY * offsetSensitivity;

            this.lastMouseX = pointerInfo.event.offsetX;
            this.lastMouseY = pointerInfo.event.offsetY;
          }
        }
      }

      const shouldProcessClick =
        (pointerInfo.type === PointerEventTypes.POINTERDOWN && this.cameraMode === 'free') ||
        (pointerInfo.type === PointerEventTypes.POINTERUP && (this.cameraMode === 'orbit' || this.cameraMode === 'rts') && pointerInfo.event.button === 0);

      if (shouldProcessClick) {
        const pickResult = this.scene.pick(pointerInfo.event.offsetX, pointerInfo.event.offsetY);
        this.processClick(pickResult);
      }
    });
  }

  private processClick(pickResult: any): void {
    if (pickResult && pickResult.hit && pickResult.pickedMesh) {
      const clickedMesh = pickResult.pickedMesh;

      let clickedUnit: Unit3D | null = null;
      let clickedBuilding: Building3D | null = null;

      const findUnitFromMesh = (mesh: any): Unit3D | null => {
        let currentMesh = mesh;
        while (currentMesh) {
          for (const units of Object.values(this.serverUnits)) {
            const unit = units.find(u => {
              return u.mesh === currentMesh ||
                (u.turretMesh && u.turretMesh === currentMesh) ||
                (u.barrelMesh && u.barrelMesh === currentMesh);
            });
            if (unit) {
              return unit;
            }
          }
          currentMesh = currentMesh.parent;
        }
        return null;
      };

      clickedUnit = findUnitFromMesh(clickedMesh);

      const findBuildingFromMesh = (mesh: any): Building3D | null => {
        let currentMesh = mesh;
        while (currentMesh) {
          for (const buildings of Object.values(this.serverBuildings)) {
            const building = buildings.find(b => b.mesh === currentMesh);
            if (building) {
              return building;
            }
          }
          currentMesh = currentMesh.parent;
        }
        return null;
      };

      if (!clickedUnit) {
        clickedBuilding = findBuildingFromMesh(clickedMesh);
      }

      if (clickedUnit) {
        this.selectedUnit = clickedUnit;
        this.selectedBuilding = null;
        if (this.infoPanel) {
          this.infoPanel.showUnitInstance(clickedUnit);
        }
      } else if (clickedBuilding) {
        this.selectedBuilding = clickedBuilding;
        this.selectedUnit = null;
        if (this.infoPanel) {
          this.infoPanel.showBuildingInstance(clickedBuilding);
        }
      } else {
        this.selectedUnit = null;
        this.selectedBuilding = null;
        if (this.infoPanel) {
          this.infoPanel.clearInstanceSelection();
        }
      }
    } else {
      this.selectedUnit = null;
      this.selectedBuilding = null;
      if (this.infoPanel) {
        this.infoPanel.clearInstanceSelection();
      }
    }
  }

  /**
   * Creates a base mesh and two instances with materials (own and enemy)
   * @param baseName Base name for the mesh
   * @param builder Function to create the base mesh
   * @param getOwnColor Function to get the own player color
   * @param getEnemyColor Function to get the enemy color
   * @returns Array of [own instance, enemy instance]
   */
  private createPlayerColoredMesh(
    baseName: string,
    builder: () => Mesh,
    getOwnColor: () => Color3,
    getEnemyColor: () => Color3
  ): Mesh[] {

    // Create own instance with material
    const ownInstance = builder();
    const ownMaterial = new StandardMaterial(baseName + "Mat_own", this.scene);
    ownMaterial.diffuseColor = getOwnColor();
    ownInstance.material = ownMaterial;

    // Create enemy instance with material
    const enemyInstance = builder();
    const enemyMaterial = new StandardMaterial(baseName + "Mat_enemy", this.scene);
    enemyMaterial.diffuseColor = getEnemyColor();
    enemyInstance.material = enemyMaterial;

    return [ownInstance, enemyInstance];
  }

  private createBaseMeshes(): void {
    this.baseMeshes.targetBeacon = MeshBuilder.CreateBox("targetBeacon_base", { width: 0.2, height: 2, depth: 0.2 }, this.scene);
    this.baseMeshes.positionBeacon = MeshBuilder.CreateBox("positionBeacon_base", { width: 0.2, height: 1, depth: 0.2 }, this.scene);

    // Create player-colored meshes (arrays with [own, enemy])
    this.baseMeshes.worker = this.createPlayerColoredMesh(
      "worker",
      () => MeshBuilder.CreateBox("worker_base", { width: 0.6, height: 0.6, depth: 0.4 }, this.scene),
      () => new Color3(0.2, 0.2, 0.6),  // Own: blue
      () => new Color3(0.6, 0.2, 0.2)   // Enemy: red
    );

    this.baseMeshes.tank = this.createPlayerColoredMesh(
      "tank",
      () => MeshBuilder.CreateBox("tank_base", { width: 0.8, height: 0.5, depth: 0.8 }, this.scene),
      () => new Color3(0.2, 0.2, 0.8),  // Own: blue
      () => new Color3(0.8, 0.2, 0.2)   // Enemy: red
    );

    this.baseMeshes.tankTurret = this.createPlayerColoredMesh(
      "tankTurret",
      () => MeshBuilder.CreateBox("tank_turret_base", { width: 0.5, height: 0.1, depth: 0.5 }, this.scene),
      () => new Color3(0.2, 0.2, 0.6),  // Own: lighter blue
      () => new Color3(0.6, 0.2, 0.2)   // Enemy: lighter red
    );

    this.baseMeshes.tankBarrel = this.createPlayerColoredMesh(
      "tankBarrel",
      () => MeshBuilder.CreateCylinder("tank_barrel_base", { height: 0.8, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene),
      () => new Color3(0.2, 0.2, 0.4),  // Own: darker blue
      () => new Color3(0.4, 0.2, 0.2)   // Enemy: darker red
    );

    this.baseMeshes.artillery = this.createPlayerColoredMesh(
      "artillery",
      () => MeshBuilder.CreateBox("artillery_base", { width: 0.6, height: 0.4, depth: 0.8 }, this.scene),
      () => new Color3(0.2, 0.2, 0.8),  // Own: blue
      () => new Color3(0.8, 0.2, 0.2)   // Enemy: red
    );

    this.baseMeshes.artilleryBarrel = this.createPlayerColoredMesh(
      "artilleryBarrel",
      () => MeshBuilder.CreateCylinder("artillery_barrel_base", { height: 1.2, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene),
      () => new Color3(0.2, 0.2, 0.4),  // Own: darker blue
      () => new Color3(0.4, 0.2, 0.2)   // Enemy: darker red
    );

    this.baseMeshes.crawler = this.createPlayerColoredMesh(
      "crawler",
      () => MeshBuilder.CreateBox("crawler_base", { width: 0.6, height: 0.4, depth: 0.6 }, this.scene),
      () => new Color3(0.2, 0.2, 0.8),  // Own: blue
      () => new Color3(0.8, 0.2, 0.2)   // Enemy: red
    );

    this.baseMeshes.scoutPlane = this.createPlayerColoredMesh(
      "scoutPlane",
      () => MeshBuilder.CreateBox("scoutPlane_base", { width: 0.8, height: 0.2, depth: 0.6 }, this.scene),
      () => new Color3(0.3, 0.5, 0.8),  // Own: light blue
      () => new Color3(0.8, 0.3, 0.3)   // Enemy: light red
    );

    this.baseMeshes.hub = this.createPlayerColoredMesh(
      "hub",
      () => MeshBuilder.CreateBox("hub_base", { width: 4, height: 2, depth: 4 }, this.scene),
      () => new Color3(0.2, 0.6, 0.8),  // Own: blue
      () => new Color3(0.8, 0.2, 0.2)   // Enemy: red
    );

    this.baseMeshes.unitEconomy = MeshBuilder.CreateCylinder("unitEconomy_base", { height: 0.8, diameterTop: 0.5, diameterBottom: 0.6 }, this.scene);
    this.baseMeshes.unitMilitary = MeshBuilder.CreateCylinder("unitMilitary_base", { height: 1, diameterTop: 1, diameterBottom: 0.5 }, this.scene);

    this.baseMeshes.house = MeshBuilder.CreateBox("house_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.cannon = MeshBuilder.CreateBox("cannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.cannonBarrel = MeshBuilder.CreateBox("cannonBarrel_base", { width: 1, height: 0.3, depth: 0.3 }, this.scene);
    this.baseMeshes.laserCannon = MeshBuilder.CreateBox("laserCannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.laserCannonBarrel = MeshBuilder.CreateCylinder("laserCannonBarrel_base", { height: 0.5, diameterTop: 0.2, diameterBottom: 0.2 }, this.scene);
    this.baseMeshes.missileCannon = MeshBuilder.CreateBox("missileCannon_base", { width: 1, height: 2, depth: 1 }, this.scene);
    this.baseMeshes.missileCannonBarrel = MeshBuilder.CreateBox("missileCannonBarrel_base", { width: 0.4, height: 0.6, depth: 0.4 }, this.scene);
    this.baseMeshes.oreProcessor = MeshBuilder.CreateBox("oreProcessor_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.building = MeshBuilder.CreateBox("building_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.spark = MeshBuilder.CreateBox("spark_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.sparkAttack = MeshBuilder.CreateBox("sparkAttack_base", { width: 1, height: 1, depth: 1 }, this.scene);
    this.baseMeshes.rocket = MeshBuilder.CreateBox("rocket_base", { width: 0.3, height: 1.2, depth: 0.3 }, this.scene);
    this.baseMeshes.laserBeam = MeshBuilder.CreateCylinder("laserBeam_base", { height: 1, diameterTop: 0.1, diameterBottom: 0.1 }, this.scene);
    this.baseMeshes.missile = MeshBuilder.CreateBox("missile_base", { width: 0.2, height: 0.6, depth: 0.2 }, this.scene);

    this.baseMeshes.ore = MeshBuilder.CreateCylinder("ore_base", { height: 0.5, diameterTop: 0.5, diameterBottom: 2 }, this.scene);
    this.baseMeshes.ore.animations.push(this.animations.oreAnimation);

    this.baseMeshes.wall = MeshBuilder.CreateBox("wall_base", { width: 1, height: 0.5, depth: 1 }, this.scene);

    this.baseMeshes.solarFarm = MeshBuilder.CreateBox("solarFarm_base", { width: 0.5, height: 0.5, depth: 0.5 }, this.scene);

    this.baseMeshes.solarFarmPanel = MeshBuilder.CreateBox("solarFarmPanel_base", { width: 2, height: 0.2, depth: 2 }, this.scene);

    // Set non-array meshes to invisible
    Object.entries(this.baseMeshes).forEach(([key, mesh]) => {
      if (!Array.isArray(mesh)) {
        mesh.isVisible = false;
      }
    });
  }

  private createMaterials(): void {
    this.materials.unitEconomy = new StandardMaterial("unitEconomyMat", this.scene);
    this.materials.unitEconomy.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.unitMilitary = new StandardMaterial("unitMilitaryMat", this.scene);
    this.materials.unitMilitary.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.house = new StandardMaterial("houseMat", this.scene);
    this.materials.house.diffuseColor = new Color3(0.6, 0.4, 0.2);

    this.materials.cannon = new StandardMaterial("cannonMat", this.scene);
    this.materials.cannon.diffuseColor = new Color3(0.4, 0.4, 0.4);

    this.materials.cannonBarrel = new StandardMaterial("cannonBarrelMat", this.scene);
    this.materials.cannonBarrel.diffuseColor = new Color3(0.4, 0.4, 0.4);

    this.materials.laserCannon = new StandardMaterial("laserCannonMat", this.scene);
    this.materials.laserCannon.diffuseColor = new Color3(0.2, 0.4, 0.8);

    this.materials.laserCannonBarrel = new StandardMaterial("laserCannonBarrelMat", this.scene);
    this.materials.laserCannonBarrel.diffuseColor = new Color3(0.1, 0.3, 0.9);
    this.materials.laserCannonBarrel.emissiveColor = new Color3(0.2, 0.4, 0.8);

    this.materials.missileCannon = new StandardMaterial("missileCannonMat", this.scene);
    this.materials.missileCannon.diffuseColor = new Color3(0.5, 0.3, 0.2);

    this.materials.missileCannonBarrel = new StandardMaterial("missileCannonBarrelMat", this.scene);
    this.materials.missileCannonBarrel.diffuseColor = new Color3(0.4, 0.2, 0.1);

    this.materials.oreProcessor = new StandardMaterial("oreProcessorMat", this.scene);
    this.materials.oreProcessor.diffuseColor = new Color3(0.8, 0.6, 0.2);

    this.materials.building = new StandardMaterial("buildingMat", this.scene);
    this.materials.building.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.ore = new StandardMaterial("oreMat", this.scene);
    this.materials.ore.diffuseColor = new Color3(0.8, 0.1, 0.1);
    this.materials.ore.specularColor = new Color3(0.5, 0.6, 0.87);
    this.materials.ore.alpha = 0.4;

    this.materials.wall = new StandardMaterial("wallMat", this.scene);
    this.materials.wall.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.solarFarm = new StandardMaterial("solarFarmMat", this.scene);
    this.materials.solarFarm.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.solarFarmPanel = new StandardMaterial("solarFarmPanelMat", this.scene);
    this.materials.solarFarmPanel.diffuseColor = new Color3(0.5, 0.5, 0.5);

    this.materials.spark = new StandardMaterial("sparkMat", this.scene);
    this.materials.spark.diffuseColor = new Color3(1, 1, 1);
    this.materials.spark.specularColor = new Color3(1, 1, 1);

    this.materials.sparkAttack = new StandardMaterial("sparkAttackMat", this.scene);
    this.materials.sparkAttack.diffuseColor = new Color3(1, 0, 0);
    this.materials.sparkAttack.specularColor = new Color3(1, 0, 0);

    this.materials.rocket = new StandardMaterial("rocketMat", this.scene);
    this.materials.rocket.diffuseColor = new Color3(0.8, 0.2, 0.2);
    this.materials.rocket.emissiveColor = new Color3(0.5, 0.1, 0.1);

    this.materials.laserBeam = new StandardMaterial("laserBeamMat", this.scene);
    this.materials.laserBeam.diffuseColor = new Color3(0.1, 0.3, 0.9);
    this.materials.laserBeam.emissiveColor = new Color3(0.2, 0.4, 1.0);
    this.materials.laserBeam.alpha = 0.8;

    this.materials.missile = new StandardMaterial("missileMat", this.scene);
    this.materials.missile.diffuseColor = new Color3(0.9, 0.7, 0.2);
    this.materials.missile.emissiveColor = new Color3(0.7, 0.5, 0.1);

    this.materials.targetBeacon = new StandardMaterial("targetBeaconMat", this.scene);
    this.materials.targetBeacon.diffuseColor = new Color3(0.2, 0.8, 0.8);
    this.materials.positionBeacon = new StandardMaterial("positionBeaconMat", this.scene);
    this.materials.positionBeacon.diffuseColor = new Color3(0.8, 0.8, 0.2);

    this.baseMeshes.targetBeacon.material = this.materials.targetBeacon;
    this.baseMeshes.positionBeacon.material = this.materials.positionBeacon;
    this.baseMeshes.unitEconomy.material = this.materials.unitEconomy;
    this.baseMeshes.unitMilitary.material = this.materials.unitMilitary;
    this.baseMeshes.house.material = this.materials.house;
    this.baseMeshes.cannon.material = this.materials.cannon;
    this.baseMeshes.cannonBarrel.material = this.materials.cannonBarrel;
    this.baseMeshes.laserCannon.material = this.materials.laserCannon;
    this.baseMeshes.laserCannonBarrel.material = this.materials.laserCannonBarrel;
    this.baseMeshes.missileCannon.material = this.materials.missileCannon;
    this.baseMeshes.missileCannonBarrel.material = this.materials.missileCannonBarrel;
    this.baseMeshes.oreProcessor.material = this.materials.oreProcessor;
    this.baseMeshes.building.material = this.materials.building;
    this.baseMeshes.ore.material = this.materials.ore;
    this.baseMeshes.spark.material = this.materials.spark;
    this.baseMeshes.sparkAttack.material = this.materials.sparkAttack;
    this.baseMeshes.wall.material = this.materials.wall;
    this.baseMeshes.solarFarm.material = this.materials.solarFarm;
    this.baseMeshes.solarFarmPanel.material = this.materials.solarFarmPanel;
    this.baseMeshes.rocket.material = this.materials.rocket;
    this.baseMeshes.laserBeam.material = this.materials.laserBeam;
    this.baseMeshes.missile.material = this.materials.missile;
  }

  createAnimations() {
    this.animations.oreAnimation = new BJSAnimation("oreAnimation", "position.y",
      60,
      BJSAnimation.ANIMATIONTYPE_FLOAT,
      BJSAnimation.ANIMATIONLOOPMODE_YOYO,
    );
    this.animations.oreAnimation.setKeys([
      {
        frame: 0,
        value: 0.3
      },
      {
        frame: 60,
        value: 0.5
      }
    ]);
  }

  callback = (id: number, socket: any) => {
    this.worldPlayerId = id;
    this.socket = socket;
    console.log(`Connected to server as player ${id}`);
    socket.emit('getOres', id);
    const codeButton = document.getElementById('run');
    if (codeButton && this.worldPlayerId) {
      codeButton.style.display = 'inline-block';
    }
    const respawnButton = document.getElementById('respawn');
    if (respawnButton && this.worldPlayerId) {
      respawnButton.style.display = 'inline-block';
      this.updateRespawnButtonState();
    }
  };

  tickCallback = (ticks: any) => {
    console.log('Received tick update');
  };

  terrainCallback = (terrain: any) => {
    console.log('Received terrain update');
  };

  oresCallback = (ores: any[]) => {
    const oresToRemove = this.ores.filter(
      existingOre => !ores.find(newOre => newOre.id === existingOre.id)
    );

    oresToRemove.forEach(ore => {
      ore.mesh.dispose();
      this.ores.splice(this.ores.indexOf(ore), 1);
    });

    let that = this;

    ores.forEach(ore => {
      const existingOre = this.ores.find(o => o.id === ore.id);

      if (existingOre) {
        existingOre.ore = ore.ore;
        const scale = Math.max(0.3, ore.ore / 100);
        existingOre.mesh.scaling = new Vector3(scale, scale, scale);

        // Update position and rotation to ensure alignment with sector A1
        existingOre.mesh.position = this.worldToSectorA1Position(ore.x, 0.5, ore.y);
        this.alignEntityToPlanet(existingOre.mesh);

        const animation = that.scene.beginAnimation(existingOre.mesh, 0, 60, true);
        const randomFrame = Math.floor(Math.random() * 60);
        animation.goToFrame(randomFrame);
      } else {
        const oreInstance = this.baseMeshes.ore.createInstance("ore_" + ore.id);

        oreInstance.position = this.worldToSectorA1Position(ore.x, 0.5, ore.y);
        this.alignEntityToPlanet(oreInstance);

        this.ores.push({
          mesh: oreInstance,
          id: ore.id,
          x: ore.x / this.COORDINATE_SCALE,
          y: ore.y / this.COORDINATE_SCALE,
          ore: ore.ore
        });
      }
    });

    if (this.fogOfWar) {
      this.ores.forEach(ore => {
        const worldX = ore.x * this.COORDINATE_SCALE;
        const worldY = ore.y * this.COORDINATE_SCALE;
        const visibility = this.fogOfWar.getVisibility(worldX, worldY);
        ore.mesh.isVisible = visibility !== 0 || SHOW_ALL_ORE; // 0 = UNEXPLORED
      });
    }

    console.log(`Updated ores: ${this.ores.length} total`);
  };

  dataCallback = (data: any) => {
    const dataSizeElement = document.getElementById('hud-data-size');
    if (dataSizeElement) {
      dataSizeElement.textContent = data.length.toString() + " b";
    }
  };

  resourcesCallback = (resources: any) => {
    const { ore, metal, energy, energyLimit, control, computeTime } = resources;
    const oreElement = document.getElementById('hud-ore');
    const metalElement = document.getElementById('hud-metal');
    const metalPerMinElement = document.getElementById('hud-metal-permin');
    const energyElement = document.getElementById('hud-energy');
    const energyPerMinElement = document.getElementById('hud-energy-permin');
    const controlElement = document.getElementById('hud-control');
    const computeTimeElement = document.getElementById('hud-compute-time');
    if (oreElement) {
      oreElement.textContent = ore.toString();
    }

    let energyPerMinute = 0;
    let metalPerMinute = 0;

    if (this.worldPlayerId >= 0) {
      const myBuildings = this.serverBuildings[this.worldPlayerId] || [];
      const hubCount = myBuildings.filter(b => b.k === BuildingType.Hub).length;
      const solarFarmCount = myBuildings.filter(b => b.k === BuildingType.SolarFarm).length;
      const oreProcessorCount = myBuildings.filter(b => b.k === BuildingType.OreProcessor).length;

      energyPerMinute = (hubCount + solarFarmCount) * 15;

      const metalConversionCapacity = (hubCount + oreProcessorCount) * 15;
      const oreGenerationRate = hubCount * 12;

      if (ore > 0) {
        const sustainableRate = Math.min(metalConversionCapacity, oreGenerationRate);
        const oreConsumptionPerCycle = hubCount + oreProcessorCount;
        const cyclesUntilDepletion = oreConsumptionPerCycle > 0 ? Math.floor(ore / oreConsumptionPerCycle) : Infinity;

        if (cyclesUntilDepletion > 10) {
          metalPerMinute = sustainableRate;
        } else if (cyclesUntilDepletion > 0) {
          metalPerMinute = Math.min(metalConversionCapacity, ore * 15);
        } else {
          metalPerMinute = 0;
        }
      } else {
        metalPerMinute = 0;
      }
    }

    if (metalElement) {
      metalElement.textContent = metal.toString();
    }
    if (metalPerMinElement) {
      metalPerMinElement.textContent = metalPerMinute > 0
        ? `+${Math.round(metalPerMinute)}/min`
        : '';
    }
    if (energyElement) {
      if (energyLimit !== undefined) {
        energyElement.textContent = `${energy}/${energyLimit}`;
      } else {
        energyElement.textContent = energy.toString();
      }
    }
    if (energyPerMinElement) {
      energyPerMinElement.textContent = energyPerMinute > 0
        ? `+${Math.round(energyPerMinute)}/min`
        : '';
    }
    if (controlElement) {
      controlElement.textContent = control.toString();
    }
    if (computeTimeElement) {
      computeTimeElement.textContent = computeTime.toString() + "/500";
    }

    // Update game object count
    const gameObjectCountElement = document.getElementById('hud-game-object-count');
    if (gameObjectCountElement) {
      let countString = '';
      countString += `L${Object.keys(this.debugLines).length},`;
      countString += `S${this.sparks.length},`;
      countString += `O${this.ores.length},`;
      countString += `U${Object.values(this.serverUnits).reduce((acc, units) => acc + units.length, 0)},`;
      countString += `B${Object.values(this.serverBuildings).reduce((acc, buildings) => acc + buildings.length, 0)}`;
      gameObjectCountElement.textContent = countString;
    }
  };

  processSyncQueue = () => {
    // This will be overridden by connectToServer
    // Stub implementation to satisfy TypeScript
  };

  queueRender = (messageType: string, payload: any) => {
    // Handle different message types
    switch (messageType) {
      case 'spawnUnit':
      case 'spawnWorker':
      case 'spawnTank':
        this.handleUnitSpawn(payload);
        break;
      case 'move':
        this.handleUnitMove(payload);
        break;
      case 'spawnBuilding':
        this.handleBuildingSpawn(payload);
        break;
      // Add other message types as needed
    }
  };

  // Method to update unit/building life from sync data
  updateUnitLife(playerId: number, id: number, life: number) {
    const unit = this.serverUnits[playerId]?.find(u => u.id === id);
    if (unit) {
      unit.life = life;
      // Update panel if this unit is selected
      if (this.selectedUnit && this.selectedUnit.id === id && this.selectedUnit.playerId === playerId) {
        if (this.infoPanel) {
          this.infoPanel.showUnitInstance(unit);
        }
      }
    }
  }

  updateBuildingLife(playerId: number, id: number, life: number) {
    const building = this.serverBuildings[playerId]?.find(b => b.id === id);
    if (building) {
      building.life = life;
      // Update panel if this building is selected
      if (this.selectedBuilding && this.selectedBuilding.id === id && this.selectedBuilding.playerId === playerId) {
        if (this.infoPanel) {
          this.infoPanel.showBuildingInstance(building);
        }
      }
    }
  }

  renderAttack = (playerId: number, sourceUnit: any, attackedUnit: any) => {
    const sourceX = sourceUnit.x / this.COORDINATE_SCALE;
    const sourceY = sourceUnit.y / this.COORDINATE_SCALE;
    const sourceK = sourceUnit.k;
    const attackedX = attackedUnit.x / this.COORDINATE_SCALE;
    const attackedY = attackedUnit.y / this.COORDINATE_SCALE;
    const attackedK = attackedUnit.k;

    // Check if this is a building attack and determine weapon type
    const building = this.serverBuildings[playerId]?.find(b => b.id === sourceUnit.id);
    if (building) {
      if (building.k === BuildingType.LaserCannon) {
        // Render laser beam
        this.renderLaserBeam(sourceX, sourceY, attackedX, attackedY);
      } else if (building.k === BuildingType.MissileCannon) {
        // Render missile
        this.renderMissile(sourceX, sourceY, attackedX, attackedY);
      } else {
        // Default to sparks for other cannons
        this.renderSparks(playerId, { x: sourceX, y: sourceY, k: sourceK }, { x: attackedX, y: attackedY, k: attackedK }, true);
      }

      // Rotate cannon barrel to target
      if (building.k === BuildingType.Cannon || building.k === BuildingType.LaserCannon || building.k === BuildingType.MissileCannon) {
        // Calculate angle in sector A1's coordinate space
        const worldDx = attackedX - sourceX;
        const worldDy = attackedY - sourceY;
        let angle = this.calculateSectorA1Rotation(worldDx, worldDy);
        const cannonBarrel = (building as any).cannonBarrel;
        if (cannonBarrel) {
          // It's sin for x and cos for z I guess
          const offsetX = Math.sin(angle) * 0.5;
          const offsetZ = Math.cos(angle) * 0.5;
          cannonBarrel.position = new Vector3(offsetX, 1.15, offsetZ);
          // Rotation is relative to the building's base rotation (which aligns with sector A1)
          cannonBarrel.rotation = new Vector3(0, angle + Math.PI / 2, 0);
        }
      }
    } else {
      // Unit attack - use sparks
      this.renderSparks(playerId, { x: sourceX, y: sourceY, k: sourceK }, { x: attackedX, y: attackedY, k: attackedK }, true);
    }
  };

  renderSparks = (sourcePlayerId: number, source: { x: number, y: number, k: number }, target: { x: number, y: number, k: number }, isAttack: boolean = false) => {
    // Temporarily disabled for performance
    return;
    if (!source || !target) {
      return;
    }
    const { x, y, k: sk } = source;
    const { x: tx, y: ty, k: tk } = target;
    const distance = Math.sqrt((x - tx) * (x - tx) + (y - ty) * (y - ty));
    const sparkCount = isAttack ? 3 : 10;

    // Remove oldest sparks if we're at the cap
    if (this.sparks.length + sparkCount > this.MAX_SPARKS) {
      const sparksToRemove = this.sparks.length + sparkCount - this.MAX_SPARKS;
      for (let i = 0; i < sparksToRemove; i++) {
        if (this.sparks.length > 0) {
          this.sparks[0].dispose();
          this.sparks.shift();
        }
      }
    }

    const idOffset = this.sparks.length;
    for (let i = 0; i < sparkCount; i++) {
      // Spred out sparks around the target
      const proportion = i / sparkCount;
      let angle = proportion * Math.PI * 2;
      angle += -Math.PI / 8 + Math.random() * Math.PI / 4;
      let spawnX = x;
      let spawnY = y;
      let sparkTargetX = tx;
      let sparkTargetY = ty;
      if (isAttack) {
        spawnX = x;
        spawnY = y;
      } else {
        spawnX = x + Math.cos(angle) * 10;
        spawnY = y + Math.sin(angle) * 10;
        sparkTargetX = tx + Math.cos(angle) * 10;
        sparkTargetY = ty + Math.sin(angle) * 10;
      }

      const sparkId = idOffset + i;
      const spark = isAttack ? this.baseMeshes.sparkAttack.createInstance("sparkAttack_" + sparkId) : this.baseMeshes.spark.createInstance("spark_" + sparkId);
      const heightOffset = sk === UnitType.ScoutPlane ? 8 : 1;
      const targetHeightOffset = tk === UnitType.ScoutPlane ? 8 : 1;
      spark.position = this.worldToSectorA1Position(spawnX, spawnY, heightOffset);
      const targetPos = this.worldToSectorA1Position(sparkTargetX, sparkTargetY, targetHeightOffset);
      spark.scaling = new Vector3(0.2, 0.2, 0.2);
      spark.material = isAttack ? this.materials.sparkAttack : this.materials.spark;
      (spark as any).isAttack = isAttack;
      (spark as any).targetPosition = targetPos; // Store transformed target position
      (spark as any).sk = sk;
      (spark as any).tk = tk;
      (spark as any).lifetimeTicks = 60;
      (spark as any).velocity = 20 - 0.5 * i;
      this.sparks.push(spark);
    }
  };

  private handleUnitSpawn(payload: any) {
    const { playerId, id, k, x, y } = payload;
    if (!this.serverUnits[playerId]) {
      this.serverUnits[playerId] = [];
    }

    // Is this my unit or someone elses
    const isMyUnit = Number(playerId) === this.worldPlayerId;
    const playerIndex = isMyUnit ? 0 : 1;  // 0 = own, 1 = enemy

    // Create unit instance from pre-materialized mesh array
    let unitInstance = null;
    if (k === UnitType.Worker) {
      unitInstance = this.baseMeshes.worker[playerIndex].createInstance("worker_" + id);
    } else if (k === UnitType.Tank) {
      unitInstance = this.baseMeshes.tank[playerIndex].createInstance("tank_" + id);
    } else if (k === UnitType.Crawler) {
      unitInstance = this.baseMeshes.crawler[playerIndex].createInstance("crawler_" + id);
    } else if (k === UnitType.Artillery) {
      unitInstance = this.baseMeshes.artillery[playerIndex].createInstance("artillery_" + id);
    } else if (k === UnitType.ScoutPlane) {
      unitInstance = this.baseMeshes.scoutPlane[playerIndex].createInstance("scoutPlane_" + id);
    } else {
      unitInstance = this.baseMeshes.unitMilitary.createInstance("unitMilitary_" + id);
    }
    // Air units are positioned higher
    const heightOffset = k === UnitType.ScoutPlane ? 8 : 0.5;
    this.positionAndAlignToPlanetSurface(unitInstance, x, y, heightOffset);
    const that = this;
    const unit: Unit3D = {
      mesh: unitInstance,
      id,
      playerId,
      k,
      x,
      y,
      tx: x,
      ty: y,
      vx: 0,
      vy: 0,
      currentFacing: 0, // Initialize facing direction
      life: payload.life !== undefined ? payload.life : UNIT_DEFINITIONS[k].life, // Initialize life from payload or default
      init: () => { },
      destroy: () => {
        unitInstance.dispose();
        if (unitInstance.getChildMeshes().length > 0) {
          unitInstance.getChildMeshes().forEach(child => {
            child.dispose();
          });
        }
        if ((unit as any).targetBeacon) {
          (unit as any).targetBeacon.dispose();
          delete (unit as any).targetBeacon;
        }
        if ((unit as any).positionBeacon) {
          (unit as any).positionBeacon.dispose();
          delete (unit as any).positionBeacon;
        }
        if ((unit as any).turretMesh) {
          (unit as any).turretMesh.dispose();
          delete (unit as any).turretMesh;
        }
        if ((unit as any).barrelMesh) {
          (unit as any).barrelMesh.dispose();
          delete (unit as any).barrelMesh;
        }
        that.renderSparks(playerId, { x, y, k }, { x, y, k });
        // Remove the unit from the server units
        that.serverUnits[playerId] = that.serverUnits[playerId].filter(u => u.id !== id);
        // Remove the unit from the debug lines
        let line = that.debugLines[id];
        if (line) {
          line.dispose();
        }
        delete that.debugLines[id];
        // Remove range overlays
        const key = `u${playerId}_${id}`;
        const visionKey = `vision_${key}`;
        const attackKey = `attack_${key}`;
        const stanceKey = `stance_${key}`;
        const guardLineKey = `guard_${key}`;
        if (that.visionRangeOverlays[visionKey]) {
          that.visionRangeOverlays[visionKey].dispose();
          delete that.visionRangeOverlays[visionKey];
        }
        if (that.attackRangeOverlays[attackKey]) {
          that.attackRangeOverlays[attackKey].dispose();
          delete that.attackRangeOverlays[attackKey];
        }
        if (that.stanceIndicators[stanceKey]) {
          that.stanceIndicators[stanceKey].dispose();
          delete that.stanceIndicators[stanceKey];
        }
        if (that.guardPositionLines[guardLineKey]) {
          that.guardPositionLines[guardLineKey].dispose();
          delete that.guardPositionLines[guardLineKey];
        }
        // Remove healthbar
        if (that.healthbars[key]) {
          that.healthbars[key].mesh.dispose();
          that.healthbars[key].texture.dispose();
          delete that.healthbars[key];
        }
      }
    };

    if (k === UnitType.Tank) {
      const turretInstance = this.baseMeshes.tankTurret[playerIndex].createInstance("tankTurret_" + id);
      unitInstance.addChild(turretInstance);
      turretInstance.position = new Vector3(0, 0.4, 0);
      (unitInstance as any).turretMesh = turretInstance;

      const barrelInstance = this.baseMeshes.tankBarrel[playerIndex].createInstance("tankBarrel_" + id);
      unitInstance.addChild(barrelInstance);
      barrelInstance.position = new Vector3(0, 0.4, 0.4);
      barrelInstance.rotation = new Vector3(-Math.PI / 2, 0, 0);
      (unitInstance as any).barrelMesh = barrelInstance;
    }

    if (k === UnitType.Artillery) {
      // barrel
      const barrelInstance = this.baseMeshes.artilleryBarrel[playerIndex].createInstance("artilleryBarrel_" + id);
      unitInstance.addChild(barrelInstance);
      barrelInstance.position = new Vector3(0, 0.3, 0.2);
      barrelInstance.rotation = new Vector3(-Math.PI / 2 - Math.PI / 6, 0, 0);
      (unitInstance as any).barrelMesh = barrelInstance;
    }

    if (k === UnitType.Tank || k === UnitType.Artillery || k === UnitType.Crawler || k === UnitType.ScoutPlane) {
      // Add targetBeacon child mesh so we can see server actual tx/ty
      if (SHOW_MOVEMENT_PILLARS) {
        const targetBeacon = this.baseMeshes.targetBeacon.createInstance("targetBeacon_" + id);
        targetBeacon.position = this.worldToSectorA1Position(x, y, 0.2);
        (unitInstance as any).targetBeacon = targetBeacon;
      } else {
        (unitInstance as any).targetBeacon = null;
      }
      // Change unit's tx and ty setters to update targetBeacon position too
      // As property setters...
      if (SHOW_MOVEMENT_PILLARS) {
        const that = this;
        delete unit.tx;
        delete unit.ty;
        Object.defineProperty(unit, 'tx', {
          get: () => (unit as any)._tx,
          set: (tx: number) => {
            (unit as any)._tx = tx;
            if (SHOW_MOVEMENT_PILLARS && (unit as any)._tx !== undefined && (unit as any)._ty !== undefined) {
              (unit as any).targetBeacon.position = that.worldToSectorA1Position((unit as any)._tx, (unit as any)._ty, 0.2);
            }
          }
        });
        Object.defineProperty(unit, 'ty', {
          get: () => (unit as any)._ty,
          set: (ty: number) => {
            (unit as any)._ty = ty;
            if (SHOW_MOVEMENT_PILLARS && (unit as any)._tx !== undefined && (unit as any)._ty !== undefined) {
              (unit as any).targetBeacon.position = that.worldToSectorA1Position((unit as any)._tx, (unit as any)._ty, 0.2);
            }
          }
        });
        unit.tx = x;
        unit.ty = y;
      }

      // Add a position beacon, too
      if (SHOW_MOVEMENT_PILLARS) {
        const positionBeacon = this.baseMeshes.positionBeacon.createInstance("positionBeacon_" + id);
        positionBeacon.position = this.worldToSectorA1Position(x, y, 0.2);
        (unit as any).positionBeacon = positionBeacon;
      } else {
        (unit as any).positionBeacon = null;
      }
    }

    this.serverUnits[playerId].push(unit);

    return unit;
  }

  private handleUnitMove(payload: any) {
    const { playerId, id, x, y, life } = payload;
    const unit = this.serverUnits[playerId]?.find(u => u.id === id);
    if (unit) {
      unit.tx = x;
      unit.ty = y;
      if (life !== undefined) {
        unit.life = life;
        // Update panel if this unit is selected
        if (this.selectedUnit && this.selectedUnit.id === id && this.selectedUnit.playerId === playerId) {
          if (this.infoPanel) {
            this.infoPanel.showUnitInstance(unit);
          }
        }
      }
    }
  }

  private handleBuildingSpawn(payload: any) {
    const { playerId, id, k, x, y } = payload;
    if (!this.serverBuildings[playerId]) {
      this.serverBuildings[playerId] = [];
    }

    // Create building instance from base mesh
    const isHub = k === BuildingType.Hub;
    const isOwn = playerId === this.worldPlayerId;
    const playerIndex = isOwn ? 0 : 1;  // 0 = own, 1 = enemy

    const buildingInstance = isHub
      ? this.baseMeshes.hub[playerIndex].createInstance("hub_" + id)
      : this.baseMeshes.building.createInstance("building_" + id);
    this.positionAndAlignToPlanetSurface(buildingInstance, x, y, isHub ? 1 : 0.5);

    const that = this;
    const building: Building3D = {
      mesh: buildingInstance,
      id,
      playerId,
      k,
      x,
      y,
      life: payload.life !== undefined ? payload.life : 100, // Initialize life from payload or default
      destroy: () => {
        buildingInstance.dispose();
        // Remove healthbar
        const key = `b${playerId}_${id}`;
        if (that.healthbars[key]) {
          that.healthbars[key].mesh.dispose();
          that.healthbars[key].texture.dispose();
          delete that.healthbars[key];
        }
        // Remove the building from the server buildings
        that.serverBuildings[playerId] = that.serverBuildings[playerId].filter(b => b.id !== id);
      }
    };

    this.serverBuildings[playerId].push(building);

    return building;
  }

  private updateUnitPositions() {
    // Smooth unit movement
    // server: 200 units/second.
    // client: 200 units/second. But is 30fps / 60fps so we need to scale it.
    // 200 --> 100. 500 --> 250.
    const STANDARD_VELOCITY = 50;
    const CRAWLER_VELOCITY = 125;
    const AIR_VELOCITY = 200; // Air units move faster
    const MAX_TURNING_DELTA = Math.PI / 60; // Maximum radians per frame
    const MAX_AIR_TURNING_DELTA = Math.PI / 120; // Maximum radians per frame for air units

    // Clean up all lines if not logged in, or clean up lines for units that don't belong to the player
    if (this.worldPlayerId < 0) {
      // Not logged in - clean up all lines
      Object.keys(this.debugLines).forEach(id => {
        const line = this.debugLines[id];
        if (line) {
          line.dispose();
        }
        delete this.debugLines[id];
      });
    } else {
      // Clean up lines for units that don't belong to the player
      const myUnitIds = new Set((this.serverUnits[this.worldPlayerId] || []).map(u => u.id));
      Object.keys(this.debugLines).forEach(id => {
        if (!myUnitIds.has(Number(id))) {
          const line = this.debugLines[id];
          if (line) {
            line.dispose();
          }
          delete this.debugLines[id];
        }
      });
    }

    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {

        if (unit.tx === undefined || unit.ty === undefined) {
          unit.tx = unit.x;
          unit.ty = unit.y;
          unit.vx = 0;
          unit.vy = 0;
        } else {
          const xDelta = unit.tx - unit.x;
          const yDelta = unit.ty - unit.y;
          const targetAngle = Math.atan2(yDelta, xDelta);
          const distance = Math.sqrt(xDelta * xDelta + yDelta * yDelta);

          const isAirUnit = unit.k === UnitType.ScoutPlane;
          const maxVelocity = isAirUnit ? AIR_VELOCITY :
            (unit.k === UnitType.Crawler ? CRAWLER_VELOCITY : STANDARD_VELOCITY);
          const desiredSpeed = maxVelocity / this.GAME_FPS;
          const maxTurnRate = isAirUnit ? MAX_AIR_TURNING_DELTA : MAX_TURNING_DELTA;

          // Get current velocity direction
          const currentVelocity = Math.sqrt(unit.vx * unit.vx + unit.vy * unit.vy);
          let currentAngle = currentVelocity > 0.01 ? Math.atan2(unit.vy, unit.vx) : targetAngle;

          // Turn towards target (with max turn rate)
          let angleDiff = targetAngle - currentAngle;
          while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
          while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

          if (Math.abs(angleDiff) > maxTurnRate) {
            currentAngle += Math.sign(angleDiff) * maxTurnRate;
          } else {
            currentAngle = targetAngle;
          }

          // Future ... Air units: maintain speed, always moving
          // Ground units: decelerate when close to target, stop when reached
          let minSpeed = isAirUnit ? maxVelocity * 0.5 : 0;
          let speed: number;
          const stopDistance = desiredSpeed * 10; // Start decelerating when this close
          if (distance < stopDistance) {
            speed = Math.max(minSpeed, currentVelocity * 0.9);
            // Stop completely when very close
            if (distance < 1) {
              speed = minSpeed;
            }
          } else {
            // Accelerate towards desired speed
            speed = Math.min(currentVelocity + desiredSpeed * 0.1, desiredSpeed);
          }

          unit.vx = Math.cos(currentAngle) * speed;
          unit.vy = Math.sin(currentAngle) * speed;

          // Apply server-client desync penalty (only for crawlers)
          let ratio = 1;
          if (unit.k === 5 && unit.ax !== undefined && unit.ay !== undefined) {
            unit.ax += unit.vx;
            unit.ay += unit.vy;

            if ((unit as any).positionBeacon) {
              (unit as any).positionBeacon.position = this.worldToSectorA1Position(unit.ax, unit.ay, 0.2);
            }

            const axDelta = unit.x - unit.ax;
            const ayDelta = unit.y - unit.ay;
            const serverClientUnitDelta = Math.sqrt(axDelta * axDelta + ayDelta * ayDelta);
            if (serverClientUnitDelta > 50) {
              const axtxDelta = unit.tx - unit.ax;
              const axtyDelta = unit.ty - unit.ay;
              const serverDistance = Math.sqrt(axtxDelta * axtxDelta + axtyDelta * axtyDelta);
              if (distance > serverDistance) {
                ratio = 1.2;
              } else {
                ratio = 0.8;
              }
            }
          }

          // Apply velocity
          unit.x += unit.vx * ratio;
          unit.y += unit.vy * ratio;

          if ((unit as any).positionBeacon) {
            (unit as any).positionBeacon.position = this.worldToSectorA1Position(unit.x, unit.y, 0.2);
          }

          const heightOffset = unit.k === UnitType.ScoutPlane ? 8 : 0.5;
          this.positionAndAlignToPlanetSurface(unit.mesh, unit.x, unit.y, heightOffset);

          if ((unit.k === UnitType.Tank || unit.k === UnitType.Artillery || unit.k === UnitType.Worker || unit.k === UnitType.Crawler || unit.k === UnitType.ScoutPlane) && (unit.vx !== 0 || unit.vy !== 0)) {
            const targetAngle = this.calculateSectorA1Rotation(unit.vx, unit.vy);
            unit.currentFacing = targetAngle;
          }

          // Only show movement lines for the player's own units (or no units if not logged in)
          if (this.worldPlayerId >= 0 && unit.playerId === this.worldPlayerId) {
            let line = this.debugLines[unit.id];
            if (line) {
              const points = [this.worldToSectorA1Position(unit.x, unit.y, 0.2), this.worldToSectorA1Position(unit.tx, unit.ty, 0.2)];
              line = MeshBuilder.CreateLines(null, { points, instance: line as any, updatable: true });
            } else {
              line = MeshBuilder.CreateLines("line_" + unit.id, {
                points: [this.worldToSectorA1Position(unit.x, unit.y, 0.2), this.worldToSectorA1Position(unit.tx, unit.ty, 0.2)],
                updatable: true
              }, this.scene);
              (line as any).color = new Color3(1, 1, 1);
              this.debugLines[unit.id] = line;
            }
          } else {
            // Clean up line if it exists but unit doesn't belong to player
            const line = this.debugLines[unit.id];
            if (line) {
              line.dispose();
              delete this.debugLines[unit.id];
            }
          }

        }
      });
    });
  }

  private updateCameraPosition(): void {
    const previousPosition = this.camera.position.clone();

    if (this.cameraMode === 'orbit') {
      this.updateOrbitCamera();
    } else if (this.cameraMode === 'rts') {
      this.updateRTSCamera();
    } else {
      let speed = 1.0;
      if (this.isInSpaceLayer) {
        speed = 10;
      }

      if (this.keyState.w) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Forward()).scale(speed));
      }
      if (this.keyState.s) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Backward()).scale(speed));
      }
      if (this.keyState.a) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Left()).scale(speed));
      }
      if (this.keyState.d) {
        this.camera.position.addInPlace(this.camera.getDirection(Vector3.Right()).scale(speed));
      }
      if (this.keyState.x) {
        this.camera.position.addInPlace(new Vector3(0, -1, 0).scale(speed));
      }
      if (this.keyState.c) {
        this.camera.position.addInPlace(new Vector3(0, 1, 0).scale(speed));
      }
    }

    this.checkAndScheduleCameraSave(previousPosition);
  }

  private updateRTSCamera(): void {
    if (!this.sectorA1Center || !this.sectorA1Right || !this.sectorA1Up || !this.sectorA1Normal) {
      return;
    }

    const panAcceleration = 0.5;
    const zoomAcceleration = 2.0;
    const friction = 0.92;
    const minHeight = 50;
    const maxHeight = 1000;

    if (this.keyState.w) {
      this.rtsCameraPanVelocity.addInPlace(this.sectorA1Right.scale(panAcceleration));
    }
    if (this.keyState.s) {
      this.rtsCameraPanVelocity.addInPlace(this.sectorA1Right.scale(-panAcceleration));
    }
    if (this.keyState.a) {
      this.rtsCameraPanVelocity.addInPlace(this.sectorA1Up.scale(panAcceleration));
    }
    if (this.keyState.d) {
      this.rtsCameraPanVelocity.addInPlace(this.sectorA1Up.scale(-panAcceleration));
    }
    if (this.keyState.x) {
      this.rtsCameraZoomVelocity -= zoomAcceleration;
    }
    if (this.keyState.c) {
      this.rtsCameraZoomVelocity += zoomAcceleration;
    }

    this.rtsCameraPanVelocity.scaleInPlace(friction);
    this.rtsCameraZoomVelocity *= friction;

    if (this.rtsCameraPanVelocity.length() < 0.01) {
      this.rtsCameraPanVelocity = new Vector3(0, 0, 0);
    }
    if (Math.abs(this.rtsCameraZoomVelocity) < 0.01) {
      this.rtsCameraZoomVelocity = 0;
    }

    this.rtsCameraPosition.addInPlace(this.rtsCameraPanVelocity);
    this.rtsCameraHeight += this.rtsCameraZoomVelocity;

    this.rtsCameraHeight = Math.max(minHeight, Math.min(maxHeight, this.rtsCameraHeight));

    const cameraWorldPosition = this.sectorA1Center
      .add(this.sectorA1Right.scale(this.rtsCameraPosition.x))
      .add(this.sectorA1Up.scale(this.rtsCameraPosition.z))
      .add(this.sectorA1Normal.scale(-1 * this.rtsCameraHeight));

    const lookTarget = this.sectorA1Center
      .add(this.sectorA1Right.scale(this.rtsCameraPosition.x))
      .add(this.sectorA1Up.scale(this.rtsCameraPosition.z));

    this.camera.position = cameraWorldPosition;
    this.camera.setTarget(lookTarget);
  }

  private updateOrbitCamera(): void {
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -500;
    const PLANET_CENTER_Z = halfWidth;
    const planetCenter = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);

    const orbitAcceleration = 0.0005;
    const heightAcceleration = 0.3;
    const mouseSensitivity = 0.003;
    const friction = 0.92;
    const minRadius = 100;
    const maxRadius = 2000;
    const minElevation = 0.1;
    const maxElevation = Math.PI - 0.1;

    if (this.keyState.a) {
      this.orbitAngleVelocity -= orbitAcceleration;
    }
    if (this.keyState.d) {
      this.orbitAngleVelocity += orbitAcceleration;
    }
    if (this.keyState.w) {
      this.orbitElevationVelocity -= orbitAcceleration;
    }
    if (this.keyState.s) {
      this.orbitElevationVelocity += orbitAcceleration;
    }
    if (this.keyState.x) {
      this.orbitRadiusVelocity -= heightAcceleration;
    }
    if (this.keyState.c) {
      this.orbitRadiusVelocity += heightAcceleration;
    }

    this.orbitAngleVelocity *= friction;
    this.orbitElevationVelocity *= friction;
    this.orbitRadiusVelocity *= friction;
    this.cameraOffsetPitchVelocity *= friction;
    this.cameraOffsetYawVelocity *= friction;

    if (Math.abs(this.orbitAngleVelocity) < 0.0001) {
      this.orbitAngleVelocity = 0;
    }
    if (Math.abs(this.orbitElevationVelocity) < 0.0001) {
      this.orbitElevationVelocity = 0;
    }
    if (Math.abs(this.orbitRadiusVelocity) < 0.01) {
      this.orbitRadiusVelocity = 0;
    }
    if (Math.abs(this.cameraOffsetPitchVelocity) < 0.0001) {
      this.cameraOffsetPitchVelocity = 0;
    }
    if (Math.abs(this.cameraOffsetYawVelocity) < 0.0001) {
      this.cameraOffsetYawVelocity = 0;
    }

    this.orbitAngle += this.orbitAngleVelocity;
    this.orbitElevation += this.orbitElevationVelocity;
    this.orbitRadius += this.orbitRadiusVelocity;
    this.cameraOffsetPitch += this.cameraOffsetPitchVelocity;
    this.cameraOffsetYaw += this.cameraOffsetYawVelocity;

    this.orbitElevation = Math.max(minElevation, Math.min(maxElevation, this.orbitElevation));
    this.orbitRadius = Math.max(minRadius, Math.min(maxRadius, this.orbitRadius));
    const maxOffsetAngle = Math.PI / 3;
    this.cameraOffsetPitch = Math.max(-maxOffsetAngle, Math.min(maxOffsetAngle, this.cameraOffsetPitch));
    const x = planetCenter.x + this.orbitRadius * Math.sin(this.orbitElevation) * Math.cos(this.orbitAngle);
    const y = planetCenter.y + this.orbitRadius * Math.cos(this.orbitElevation);
    const z = planetCenter.z + this.orbitRadius * Math.sin(this.orbitElevation) * Math.sin(this.orbitAngle);

    this.camera.position = new Vector3(x, y, z);

    const planetNormal = planetCenter.subtract(this.camera.position).normalize();
    const offsetVector = planetNormal.scale(1).add(new Vector3(0, this.cameraOffsetPitch, 0)).normalize();
    const targetPoint = this.camera.position.add(offsetVector);
    this.camera.setTarget(targetPoint);
  }

  private checkAndScheduleCameraSave(previousPosition: Vector3): void {
    const currentPosition = this.camera.position;
    const distance = Vector3.Distance(previousPosition, currentPosition);

    const rotationChanged = this.lastSavedCameraPosition === null ||
      (this.camera.rotation && this.lastSavedCameraPosition &&
        (Math.abs(this.camera.rotation.x) > 0.01 || Math.abs(this.camera.rotation.y) > 0.01 || Math.abs(this.camera.rotation.z) > 0.01));

    if (this.cameraSaveTimeout === null && (distance > this.CAMERA_POSITION_EPSILON || rotationChanged)) {
      this.scheduleCameraSave();
    }
  }

  private scheduleCameraSave(): void {
    if (this.cameraSaveTimeout !== null) {
      clearTimeout(this.cameraSaveTimeout);
    }

    this.cameraSaveTimeout = window.setTimeout(() => {
      if (!this.isInSpaceLayer) {
        this.saveCameraPosition();
      }
      this.cameraSaveTimeout = null;
    }, this.CAMERA_SAVE_THROTTLE_MS);
  }

  private saveCameraPosition(): void {
    if (this.cameraMode !== 'free') {
      return;
    }

    const position = this.camera.position;
    const rotation = this.camera.rotation;
    const cameraData = {
      x: position.x,
      y: position.y,
      z: position.z,
      rotationX: rotation.x,
      rotationY: rotation.y,
      rotationZ: rotation.z,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(this.CAMERA_POSITION_KEY, JSON.stringify(cameraData));
      this.lastSavedCameraPosition = position.clone();
      console.log('Camera position and rotation saved to localStorage');
    } catch (error) {
      console.warn('Failed to save camera position to localStorage:', error);
    }
  }

  private loadCameraPosition(): void {
    if (this.cameraMode !== 'free') {
      return;
    }

    try {
      const savedData = localStorage.getItem(this.CAMERA_POSITION_KEY);
      if (savedData) {
        const cameraData = JSON.parse(savedData);
        const position = new Vector3(cameraData.x, cameraData.y, cameraData.z);

        if (position && !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)) {
          this.camera.position = position;
          this.lastSavedCameraPosition = position.clone();

          if (cameraData.rotationX !== undefined && cameraData.rotationY !== undefined && cameraData.rotationZ !== undefined) {
            const rotation = new Vector3(cameraData.rotationX, cameraData.rotationY, cameraData.rotationZ);
            if (!isNaN(rotation.x) && !isNaN(rotation.y) && !isNaN(rotation.z)) {
              this.camera.rotation = rotation;
            }
          }

          console.log('Camera position and rotation restored from localStorage');
        }
      }
    } catch (error) {
      console.warn('Failed to load camera position from localStorage:', error);
    }
  }

  /**
   */
  private setCameraMode(mode: 'free' | 'orbit' | 'rts'): void {
    if (mode === this.cameraMode) {
      return;
    }

    if (this.cameraMode === 'free') {
      this.savedFreeCameraPosition = this.camera.position.clone();
      this.savedFreeCameraRotation = this.camera.rotation ? this.camera.rotation.clone() : null;
    }

    this.isMouseDown = false;
    this.isRightMouseDown = false;
    this.hasMouseMoved = false;

    if (mode === 'free') {
      this.cameraMode = 'free';
      this.orbitAngleVelocity = 0;
      this.orbitElevationVelocity = 0;
      this.orbitRadiusVelocity = 0;
      this.cameraOffsetPitchVelocity = 0;
      this.cameraOffsetYawVelocity = 0;
      this.rtsCameraPanVelocity = new Vector3(0, 0, 0);
      this.rtsCameraZoomVelocity = 0;
      if (this.savedFreeCameraPosition) {
        this.camera.position = this.savedFreeCameraPosition.clone();
        if (this.savedFreeCameraRotation) {
          this.camera.rotation = this.savedFreeCameraRotation.clone();
        }
      }
      this.camera.attachControl(this.canvas, true);
      console.log('Switched to free camera mode');
    } else if (mode === 'orbit') {
      this.cameraMode = 'orbit';
      this.camera.detachControl();
      this.rtsCameraPanVelocity = new Vector3(0, 0, 0);
      this.rtsCameraZoomVelocity = 0;
      this.initializeOrbitCamera();
      console.log('Switched to orbit camera mode');
    } else if (mode === 'rts') {
      this.cameraMode = 'rts';
      this.camera.detachControl();
      this.orbitAngleVelocity = 0;
      this.orbitElevationVelocity = 0;
      this.orbitRadiusVelocity = 0;
      this.cameraOffsetPitchVelocity = 0;
      this.cameraOffsetYawVelocity = 0;
      this.initializeRTSCamera();
      console.log('Switched to RTS camera mode');
    }

    window.dispatchEvent(new CustomEvent('cameraModeChanged', {
      detail: { mode: this.cameraMode }
    }));
  }

  public getCameraMode(): 'free' | 'orbit' | 'rts' {
    return this.cameraMode;
  }

  private updateCameraModeButtons(): void {
    const cameraModeButtons = document.querySelectorAll('.camera-mode-btn');
    if (cameraModeButtons.length === 0) {
      return;
    }
    cameraModeButtons.forEach(button => {
      const mode = button.getAttribute('data-mode');
      const span = button.querySelector('span');
      if (mode === this.cameraMode) {
        (button as any).style.backgroundColor = '#0891b2'; // cyan-700
        if (span) {
          span.textContent = mode.charAt(0).toUpperCase() + mode.slice(1) + ' ✓';
        }
      } else {
        (button as any).style.backgroundColor = '';
        if (span) {
          span.textContent = mode.charAt(0).toUpperCase() + mode.slice(1);
        }
      }
    });
  }

  private initializeOrbitCamera(): void {
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -512;
    const PLANET_CENTER_Z = halfWidth;
    const planetCenter = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);

    const x = planetCenter.x + this.orbitRadius * Math.sin(this.orbitElevation) * Math.cos(this.orbitAngle);
    const y = planetCenter.y + this.orbitRadius * Math.cos(this.orbitElevation);
    const z = planetCenter.z + this.orbitRadius * Math.sin(this.orbitElevation) * Math.sin(this.orbitAngle);

    this.camera.position = new Vector3(x, y, z);
    this.camera.setTarget(planetCenter);
  }

  private initializeRTSCamera(): void {
    if (!this.sectorA1Center || !this.sectorA1Right || !this.sectorA1Up || !this.sectorA1Normal) {
      console.warn('Cannot initialize RTS camera: Sector A1 coordinate system not available');
      return;
    }

    this.rtsCameraPosition = new Vector3(0, 0, 0);
    this.rtsCameraHeight = 300;

    const cameraWorldPosition = this.sectorA1Center
      .add(this.sectorA1Right.scale(this.rtsCameraPosition.x))
      .add(this.sectorA1Up.scale(this.rtsCameraPosition.z))
      .add(this.sectorA1Normal.scale(-1 * this.rtsCameraHeight));

    this.camera.position = cameraWorldPosition;
    this.camera.setTarget(this.sectorA1Center);
  }

  private toggleSpaceLayer(): void {
    const SPACE_LAYER_HEIGHT = 1000;
    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;

    if (!this.isInSpaceLayer) {
      this.groundCameraPosition = this.camera.position.clone();
      this.groundCameraRotation = this.camera.rotation.clone();

      this.camera.position = new Vector3(halfWidth, SPACE_LAYER_HEIGHT + 500, halfWidth);
      this.camera.setTarget(new Vector3(halfWidth, SPACE_LAYER_HEIGHT, halfWidth));

      this.isInSpaceLayer = true;
      console.log('Switched to space layer');
    } else {
      if (this.groundCameraPosition && this.groundCameraRotation) {
        this.camera.position = this.groundCameraPosition;
        this.camera.rotation = this.groundCameraRotation;
      } else {
        this.camera.position = new Vector3(0, 100, 0);
        this.camera.setTarget(Vector3.Zero());
      }

      this.isInSpaceLayer = false;
      console.log('Switched to ground layer');
    }
  }

  public destroy(): void {
    if (this.cameraSaveTimeout !== null) {
      clearTimeout(this.cameraSaveTimeout);
      this.cameraSaveTimeout = null;
    }

    if (this.fogOfWar) {
      this.fogOfWar.dispose();
    }

    // Clean up range overlays
    Object.values(this.visionRangeOverlays).forEach(overlay => overlay.dispose());
    Object.values(this.attackRangeOverlays).forEach(overlay => overlay.dispose());
    Object.values(this.stanceIndicators).forEach(overlay => overlay.dispose());
    Object.values(this.guardPositionLines).forEach(line => line.dispose());
    Object.values(this.healthbars).forEach(healthbar => {
      healthbar.mesh.dispose();
      healthbar.texture.dispose();
    });
    Object.values(this.sectorLabels).forEach(label => {
      label.mesh.dispose();
      label.texture.dispose();
    });
    // Clean up rockets
    this.rockets.forEach(rocket => rocket.mesh.dispose());
    this.rockets = [];
    // Clean up laser beams
    this.laserBeams.forEach(beam => beam.mesh.dispose());
    this.laserBeams = [];
    // Clean up missiles
    this.missiles.forEach(missile => missile.mesh.dispose());
    this.missiles = [];
    // Clean up space ships
    this.spaceShips.forEach(ship => ship.mesh.dispose());
    this.spaceShips = [];
    this.visionRangeOverlays = {};
    this.attackRangeOverlays = {};
    this.stanceIndicators = {};
    this.guardPositionLines = {};
    this.healthbars = {};
    this.sectorLabels = {};
  }

  private updateSelectedInstancePanel(): void {
    // Update panel if a unit or building is selected (to refresh position/health)
    if (this.selectedUnit && this.infoPanel) {
      this.infoPanel.showUnitInstance(this.selectedUnit);
    } else if (this.selectedBuilding && this.infoPanel) {
      this.infoPanel.showBuildingInstance(this.selectedBuilding);
    }
  }

  private updateSparks(): void {
    const sparksToRemove = [];
    for (let i = 0; i < this.sparks.length; i++) {
      const spark = this.sparks[i];
      // Move the spark towards the target in 3D space
      const targetPos = (spark as any).targetPosition as Vector3;
      const direction = targetPos.subtract(spark.position);
      const distance = direction.length();

      if (distance > 0.1) {
        const normalized = direction.normalize();
        const moveDistance = (spark as any).velocity / this.GAME_FPS;
        spark.position.addInPlace(normalized.scale(moveDistance));
      } else {
        spark.position = targetPos.clone();
      }

      (spark as any).lifetimeTicks--;
      if (!(spark as any).isAttack) {
        (spark as any).velocity *= 0.95;
      }
      if ((spark as any).lifetimeTicks <= 0) {
        sparksToRemove.push(i);
      }
    };
    sparksToRemove.reverse();
    sparksToRemove.forEach(i => {
      this.sparks[i].dispose();
      this.sparks.splice(i, 1);
    });
  }

  /**
   * Check if orbital mass drivers should launch rockets and launch them
   */
  private checkAndLaunchRockets(): void {
    const currentTime = Date.now();
    const LAUNCH_INTERVAL_MS = 10000; // 10 seconds

    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        if (building.k === BuildingType.OrbitalMassDriver) {
          const key = `${building.playerId}_${building.id}`;
          const lastLaunch = this.orbitalMassDriverLastLaunch[key] || 0;

          if (currentTime - lastLaunch >= LAUNCH_INTERVAL_MS) {
            this.launchRocket(building);
            this.orbitalMassDriverLastLaunch[key] = currentTime;
          }
        }
      });
    });
  }

  /**
   * Launch a rocket from an orbital mass driver
   */
  private launchRocket(building: Building3D): void {
    const rocketInstance = this.baseMeshes.rocket.createInstance(`rocket_${Date.now()}_${building.id}`);

    // Position rocket at the top of the orbital mass driver
    rocketInstance.position = this.worldToSectorA1Position(building.x, building.y, 1.5);

    // Add rocket to tracking array
    this.rockets.push({
      mesh: rocketInstance,
      velocity: 0, // Units per frame
      lifetime: 300 // Frames (2 seconds at 60fps)
    });
  }

  /**
   * Render a laser beam from source to target
   */
  private renderLaserBeam(sourceX: number, sourceY: number, targetX: number, targetY: number): void {
    // Transform source and target to sector A1
    const sourcePos = this.worldToSectorA1Position(sourceX, sourceY, 1.5);
    const targetPos = this.worldToSectorA1Position(targetX, targetY, 1.5);
    const direction = targetPos.subtract(sourcePos);
    const distance = direction.length();
    const midPos = sourcePos.add(direction.scale(0.5));

    // Create a cylinder for the laser beam
    const laserBeam = MeshBuilder.CreateCylinder(
      `laserBeam_${Date.now()}`,
      { height: distance, diameterTop: 0.15, diameterBottom: 0.15, tessellation: 8 },
      this.scene
    );

    // Position the beam at the midpoint
    laserBeam.position = midPos;

    // Rotate the cylinder to point from source to target
    // Cylinder default orientation is along Y axis (vertical), we need it to point along direction
    laserBeam.lookAt(targetPos);
    // Adjust for cylinder's default orientation (pointing up)
    laserBeam.rotate(Vector3.Right(), Math.PI / 2);

    // Apply material
    const material = new StandardMaterial(`laserBeamMat_${Date.now()}`, this.scene);
    material.diffuseColor = new Color3(0.1, 0.3, 0.9);
    material.emissiveColor = new Color3(0.2, 0.4, 1.0);
    material.alpha = 0.8;
    material.disableLighting = true;
    laserBeam.material = material;

    // Add to tracking array
    this.laserBeams.push({
      mesh: laserBeam,
      lifetime: 10 // Very short lifetime for instant beam effect (10 frames = ~0.17 seconds)
    });
  }

  /**
   * Render a missile projectile from source to target
   */
  private renderMissile(sourceX: number, sourceY: number, targetX: number, targetY: number): void {
    const missileInstance = this.baseMeshes.missile.createInstance(`missile_${Date.now()}`);
    const sourcePos = this.worldToSectorA1Position(sourceX, sourceY, 1.5);
    const targetPos = this.worldToSectorA1Position(targetX, targetY, 1.5);
    missileInstance.position = sourcePos;

    // Calculate direction and velocity in 3D space
    const direction = targetPos.subtract(sourcePos);
    const distance = direction.length();
    const speed = 15; // Units per frame
    const velocity = direction.normalize().scale(speed);

    // Calculate target Z position (same as source for ground targets)
    // Rotate missile to face direction
    missileInstance.lookAt(targetPos);

    // Add to tracking array
    this.missiles.push({
      mesh: missileInstance,
      velocity: velocity,
      lifetime: Math.ceil(distance / speed) + 10, // Travel time plus buffer
      targetX: targetPos.x,
      targetY: targetPos.y,
      targetZ: targetPos.z
    });
  }

  /**
   * Update laser beam animations and remove expired beams
   */
  private updateLaserBeams(): void {
    const beamsToRemove: number[] = [];

    for (let i = 0; i < this.laserBeams.length; i++) {
      const beam = this.laserBeams[i];

      // Fade out the beam
      const material = beam.mesh.material as StandardMaterial;
      beam.lifetime--;
      material.alpha = Math.max(0, (beam.lifetime / 10) * 0.8);

      // Remove if lifetime expired
      if (beam.lifetime <= 0) {
        beamsToRemove.push(i);
      }
    }

    // Remove expired beams (reverse to maintain indices)
    beamsToRemove.reverse().forEach(i => {
      this.laserBeams[i].mesh.dispose();
      this.laserBeams.splice(i, 1);
    });
  }

  /**
   * Update missile positions and remove expired missiles
   */
  private updateMissiles(): void {
    const missilesToRemove: number[] = [];

    for (let i = 0; i < this.missiles.length; i++) {
      const missile = this.missiles[i];

      // Move missile towards target
      missile.mesh.position.addInPlace(missile.velocity);

      // Check if missile has reached target (with some tolerance)
      const currentPos = missile.mesh.position;
      const targetPos = new Vector3(missile.targetX, missile.targetY, missile.targetZ);
      const distanceToTarget = Vector3.Distance(currentPos, targetPos);

      // Decrease lifetime
      missile.lifetime--;

      // Remove if lifetime expired or reached target
      if (missile.lifetime <= 0 || distanceToTarget < 0.5) {
        // Create explosion effect at target
        // Note: targetX/Y/Z are now 3D coordinates, need to convert back to world coords for sparks
        // For now, use approximate conversion (this is a simplification)
        if (distanceToTarget < 0.5) {
          // Approximate world coordinates from 3D position (reverse transform would be complex)
          // Using a simple approximation: assume target is near sector center
          const approxWorldX = (missile.targetX - (this.sectorA1Center?.x || 0)) * this.COORDINATE_SCALE + this.WORLD_SIZE / 2;
          const approxWorldY = (missile.targetZ - (this.sectorA1Center?.z || 0)) * this.COORDINATE_SCALE + this.WORLD_SIZE / 2;
          this.renderSparks(
            -1, // No specific player
            { x: approxWorldX, y: approxWorldY, k: 0 },
            { x: approxWorldX, y: approxWorldY, k: 0 },
            true
          );
        }
        missilesToRemove.push(i);
      }
    }

    // Remove expired missiles (reverse to maintain indices)
    missilesToRemove.reverse().forEach(i => {
      this.missiles[i].mesh.dispose();
      this.missiles.splice(i, 1);
    });
  }

  private updateRockets(): void {
    const rocketsToRemove: number[] = [];

    for (let i = 0; i < this.rockets.length; i++) {
      const rocket = this.rockets[i];

      rocket.mesh.position.y += rocket.velocity;
      const maxVelocity = 5;
      let initialPhase = Math.min(rocket.lifetime, 300);
      let baseAccel = initialPhase < 300
        ? 0.001 * (1 - rocket.velocity / maxVelocity) ** 3
        : 0.003 * (1 - rocket.velocity / maxVelocity) ** 2;
      rocket.velocity += baseAccel;
      if (rocket.velocity > maxVelocity) {
        rocket.velocity = maxVelocity;
      }

      // Decrease lifetime
      rocket.lifetime--;

      if (rocket.lifetime <= 0 || rocket.mesh.position.y > 500) {
        rocketsToRemove.push(i);
      }
    }

    rocketsToRemove.reverse().forEach(i => {
      this.rockets[i].mesh.dispose();
      this.rockets.splice(i, 1);
    });
  }

  private updateSpaceShips(): void {

    this.spaceShips.forEach(ship => {
      ship.angle += ship.orbitalSpeed;
      if (ship.angle > Math.PI * 2) {
        ship.angle -= Math.PI * 2;
      }

      const x = ship.centerX + Math.cos(ship.angle) * ship.radius;
      const y = ship.centerY;
      const z = ship.centerZ + Math.sin(ship.angle) * ship.radius;
      ship.mesh.position.x = x;
      ship.mesh.position.y = y;
      ship.mesh.position.z = z;

      const dx = ship.centerX - x;
      const dy = ship.centerY - y;
      const dz = ship.centerZ - z;

      const horizontalAngle = Math.atan2(dx, dz);
      ship.mesh.rotation.y = horizontalAngle;

      const horizontalDistance = Math.sqrt(dx * dx + dz * dz);
      const pitchAngle = Math.atan2(dy, horizontalDistance) + Math.PI / 2;
      ship.mesh.rotation.x = pitchAngle;
    });
  }

  private updateSatellites(): void {

    const origin = this.planet.position;
    const radius = 900;
    // for (let i = 0; i < detail; i++) {
    //   angle = i * 2 * Math.PI / detail;
    //   const satelliteClone = satelliteMesh.clone("satelliteClone_" + i);
    //   satelliteClone.material = satelliteMaterial;
    //   satelliteClone.position = new Vector3(origin.x + Math.cos(angle) * radius, origin.y + Math.sin(angle) * radius - 5, halfWidth);
    //   satelliteClone.rotation = new Vector3(0, 0, angle - Math.PI / 2);
    //   this.satellites.push(satelliteClone);
    // }
    this.satellites.forEach((satellite: any, index: number) => {
      if (!satellite.angle) {
        satellite.angle = index * 2 * Math.PI / this.satellites.length;
        satellite.orbitalSpeed = 0.0001;
        satellite.radius = radius;
      }
      satellite.angle += satellite.orbitalSpeed;
      if (satellite.angle > Math.PI * 2) {
        satellite.angle -= Math.PI * 2;
      }

      const x = origin.x + Math.cos(satellite.angle) * satellite.radius;
      const y = origin.y + Math.sin(satellite.angle) * satellite.radius - 5;
      const z = origin.z - Math.sin(satellite.angle) * satellite.radius;
      satellite.position.x = x;
      satellite.position.y = y;
      satellite.position.z = z;
    });
  }

  private updateRangeOverlays(): void {
    // return early if we can
    if (!this.showVisionRange && !this.showAttackRange && !this.showStanceIndicators) {
      return;
    }

    const currentUnitKeys = new Set<string>();
    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {
        const key = `u${unit.playerId}_${unit.id}`;
        currentUnitKeys.add(key);

        const scaledX = unit.x / this.COORDINATE_SCALE;
        const scaledY = unit.y / this.COORDINATE_SCALE;

        if (this.showVisionRange) {
          const visionKey = `vision_${key}`;
          const visionRange = UNIT_DEFINITIONS[unit.k]?.sightRange || 300;
          this.createOrUpdateRangeOverlay(
            visionKey,
            scaledX,
            scaledY,
            visionRange / this.COORDINATE_SCALE,
            new Color3(0, 1, 0),
            this.visionRangeOverlays
          );
        }

        if (this.showAttackRange) {
          const weaponType = UNIT_DEFINITIONS[unit.k]?.defaultWeapon ?? null;
          if (weaponType) {
            const attackKey = `attack_${key}`;
            const attackRange = WeaponStatsMap[weaponType].range;
            this.createOrUpdateRangeOverlay(
              attackKey,
              scaledX,
              scaledY,
              attackRange / this.COORDINATE_SCALE,
              new Color3(1, 0, 0),
              this.attackRangeOverlays
            );
          }
        }

        if (this.showStanceIndicators) {
          const stanceKey = `stance_${key}`;
          const stance = unit.s || UnitStance.StandGround;
          let stanceColor: Color3;
          if (stance === UnitStance.StandGround) {
            stanceColor = new Color3(0.5, 0.5, 0.5);
          } else if (stance === UnitStance.Defensive) {
            stanceColor = new Color3(0, 0, 1);
          } else {
            stanceColor = new Color3(1, 0, 0);
          }
          this.createOrUpdateRangeOverlay(
            stanceKey,
            scaledX,
            scaledY,
            1.0,
            stanceColor,
            this.stanceIndicators
          );

          if (stance === UnitStance.Defensive && unit.gx !== undefined && unit.gy !== undefined) {
            const guardLineKey = `guard_${key}`;
            this.createOrUpdateGuardLine(
              guardLineKey,
              unit.x,
              unit.y,
              unit.gx,
              unit.gy,
              stanceColor
            );
          }
        }
      });
    });

    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        const key = `b${building.playerId}_${building.id}`;
        currentUnitKeys.add(key);
        const scaledX = building.x / this.COORDINATE_SCALE;
        const scaledY = building.y / this.COORDINATE_SCALE;

        if (this.showVisionRange) {
          const visionKey = `vision_${key}`;
          const visionRange = BUILDING_DEFINITIONS[building.k]?.sightRange || 300;
          this.createOrUpdateRangeOverlay(
            visionKey,
            scaledX,
            scaledY,
            visionRange / this.COORDINATE_SCALE,
            new Color3(0, 1, 0),
            this.visionRangeOverlays
          )
        }

        if (this.showAttackRange) {
          const weaponType = BUILDING_DEFINITIONS[building.k]?.defaultWeapon ?? null;
          if (weaponType) {
            const attackKey = `attack_${key}`;
            const attackRange = WeaponStatsMap[weaponType].range;

            this.createOrUpdateRangeOverlay(
              attackKey,
              scaledX,
              scaledY,
              attackRange / this.COORDINATE_SCALE,
              new Color3(1, 0, 0),
              this.attackRangeOverlays
            )
          }
        }
      });
    });

    Object.keys(this.visionRangeOverlays).forEach(key => {
      const unitKey = key.replace('vision_', '');
      if (!this.showVisionRange || !currentUnitKeys.has(unitKey)) {
        this.visionRangeOverlays[key].dispose();
        delete this.visionRangeOverlays[key];
      }
    });

    Object.keys(this.attackRangeOverlays).forEach(key => {
      const unitKey = key.replace('attack_', '');
      if (!this.showAttackRange || !currentUnitKeys.has(unitKey)) {
        this.attackRangeOverlays[key].dispose();
        delete this.attackRangeOverlays[key];
      }
    });

    Object.keys(this.stanceIndicators).forEach(key => {
      const unitKey = key.replace('stance_', '');
      if (!this.showStanceIndicators || !currentUnitKeys.has(unitKey)) {
        this.stanceIndicators[key].dispose();
        delete this.stanceIndicators[key];
      }
    });

    Object.keys(this.guardPositionLines).forEach(key => {
      const unitKey = key.replace('guard_', '');
      if (!this.showStanceIndicators || !currentUnitKeys.has(unitKey)) {
        this.guardPositionLines[key].dispose();
        delete this.guardPositionLines[key];
      }
    });
  }

  private createOrUpdateRangeOverlay(
    key: string,
    x: number,
    y: number,
    radius: number,
    color: Color3,
    overlayMap: { [key: string]: Mesh }
  ): void {
    let overlay = overlayMap[key];
    if (!overlay) {
      overlay = MeshBuilder.CreateTorus(
        `rangeOverlay_${key}`,
        {
          diameter: radius * 2,
          thickness: radius * 0.05,
          tessellation: 32
        },
        this.scene
      );
      overlay.position.y = 0.1;

      const material = new StandardMaterial(`rangeMaterial_${key}`, this.scene);
      material.diffuseColor = color;
      material.emissiveColor = color;
      material.alpha = 0.3;
      material.disableLighting = true;
      material.transparencyMode = 2;
      overlay.material = material;
      overlayMap[key] = overlay;
    }

    const material = overlay.material as StandardMaterial;
    if (color !== material.diffuseColor) {
      material.diffuseColor = color;
      material.emissiveColor = color;
    }

    overlay.position.x = x;
    overlay.position.z = y;
    overlay.isVisible = true;
  }

  private updateHealthbars(): void {
    // return early if we can
    if (!this.showHealthbars) {
      return;
    }

    const currentObjectKeys = new Set<string>();

    // Update healthbars for units
    Object.values(this.serverUnits).forEach(units => {
      units.forEach(unit => {
        const key = `u${unit.playerId}_${unit.id}`;
        currentObjectKeys.add(key);

        if (this.showHealthbars) {
          const scaledX = unit.x / this.COORDINATE_SCALE;
          const scaledY = unit.y / this.COORDINATE_SCALE;
          const healthbarY = unit.mesh.position.y + 0.8; // 0.8 units above the unit
          const maxHealth = UNIT_DEFINITIONS[unit.k]?.life || 100;
          const currentHealth = unit.life ?? maxHealth;
          const healthPercent = Math.max(0, Math.min(1, currentHealth / maxHealth));

          this.createOrUpdateHealthbar(
            key,
            scaledX,
            healthbarY,
            scaledY,
            healthPercent
          );
        }
      });
    });

    Object.values(this.serverBuildings).forEach(buildings => {
      buildings.forEach(building => {
        const key = `b${building.playerId}_${building.id}`;
        currentObjectKeys.add(key);

        if (this.showHealthbars) {
          const scaledX = building.x / this.COORDINATE_SCALE;
          const scaledY = building.y / this.COORDINATE_SCALE;
          const healthbarY = building.mesh.position.y + 1.5;
          const maxHealth = BUILDING_DEFINITIONS[building.k]?.life || 100;
          const currentHealth = building.life ?? maxHealth;
          const healthPercent = Math.max(0, Math.min(1, currentHealth / maxHealth));

          this.createOrUpdateHealthbar(
            key,
            scaledX,
            healthbarY,
            scaledY,
            healthPercent
          );
        }
      });
    });

    Object.keys(this.healthbars).forEach(key => {
      if (!this.showHealthbars || !currentObjectKeys.has(key)) {
        const healthbar = this.healthbars[key];
        if (healthbar) {
          healthbar.mesh.dispose();
          healthbar.texture.dispose();
          delete this.healthbars[key];
        }
      }
    });
  }

  private createOrUpdateHealthbar(
    key: string,
    x: number,
    y: number,
    z: number,
    healthPercent: number
  ): void {
    let healthbar = this.healthbars[key];

    if (!healthbar) {
      const healthbarPlane = MeshBuilder.CreatePlane(
        `healthbar_${key}`,
        { width: 0.8, height: 0.1 },
        this.scene
      );
      healthbarPlane.billboardMode = Mesh.BILLBOARDMODE_ALL;
      healthbarPlane.position = new Vector3(x, y, z);

      const textureSize = 128;
      const texture = new DynamicTexture(
        `healthbarTexture_${key}`,
        { width: textureSize, height: textureSize },
        this.scene,
        false
      );

      const material = new StandardMaterial(`healthbarMaterial_${key}`, this.scene);
      material.diffuseTexture = texture;
      material.emissiveTexture = texture;
      material.disableLighting = true;
      material.backFaceCulling = false;
      healthbarPlane.material = material;

      healthbar = { mesh: healthbarPlane, texture };
      this.healthbars[key] = healthbar;
    }

    healthbar.mesh.position = new Vector3(x, y, z);

    this.drawHealthbarTexture(healthbar.texture, healthPercent);

    healthbar.mesh.isVisible = this.showHealthbars;
  }

  private drawHealthbarTexture(texture: DynamicTexture, healthPercent: number): void {
    const ctx = texture.getContext();
    const width = texture.getSize().width;
    const height = texture.getSize().height;

    ctx.clearRect(0, 0, width, height);

    ctx.fillStyle = '#330000';
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    const fillWidth = Math.floor((width - 4) * healthPercent);
    if (fillWidth > 0) {
      let fillColor: string;
      if (healthPercent > 0.6) {
        fillColor = '#00ff00';
      } else if (healthPercent > 0.3) {
        fillColor = '#ffff00';
      } else {
        fillColor = '#ff0000';
      }

      ctx.fillStyle = fillColor;
      ctx.fillRect(2, 2, fillWidth, height - 4);
    }

    texture.update();
  }

  private createSectorLabel(
    key: string,
    x: number,
    y: number,
    z: number,
    labelText: string
  ): void {
    let label = this.sectorLabels[key];

    if (!label) {
      const labelPlane = MeshBuilder.CreatePlane(
        `sectorLabel_${key}`,
        { width: 20, height: 10 },
        this.scene
      );
      labelPlane.billboardMode = Mesh.BILLBOARDMODE_ALL;
      labelPlane.position = new Vector3(x, y, z);

      const textureSize = 256;
      const texture = new DynamicTexture(
        `sectorLabelTexture_${key}`,
        { width: textureSize, height: textureSize },
        this.scene,
        false
      );

      // Create material
      const material = new StandardMaterial(`sectorLabelMaterial_${key}`, this.scene);
      material.diffuseTexture = texture;
      material.emissiveTexture = texture;
      material.disableLighting = true;
      material.backFaceCulling = false;
      labelPlane.material = material;

      label = { mesh: labelPlane, texture };
      this.sectorLabels[key] = label;
    }

    // Update position
    label.mesh.position = new Vector3(x, y + 5, z);

    // Update texture with label text
    this.drawSectorLabelTexture(label.texture, labelText);

    label.mesh.isVisible = true;
  }

  private drawSectorLabelTexture(texture: DynamicTexture, labelText: string): void {
    const ctx = texture.getContext() as CanvasRenderingContext2D;
    const width = texture.getSize().width;
    const height = texture.getSize().height;

    ctx.clearRect(0, 0, width, height);

    ctx.font = 'bold 120px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 8;

    ctx.strokeText(labelText, width / 2, height / 2);
    ctx.fillText(labelText, width / 2, height / 2);

    texture.update();
  }

  private updateStars(): void {
    const dx = 500 + this.camera.position.x * 0.9;
    const dy = 500 + this.camera.position.y * 0.9;
    const dz = 500 + this.camera.position.z * 0.9;

    let now = Date.now();

    this.star.position.x = dx;
    this.star.position.y = dy;
    this.star.position.z = dz;
    this.stars.forEach((star, index) => {
      const nowish = now / 1000;
      const indexIsh = index / 10;
      let angle = nowish + indexIsh;
      let sinBit = Math.sin(angle);
      let cosBit = Math.cos(angle);
      star.position.x = dx + 3 * cosBit;
      star.position.y = dy + 3 * sinBit;
      star.position.z = dz + 3 * cosBit;
    });

    const backgroundStarDistance = 1000;
    this.backgroundStars.forEach((star, index) => {
      const direction = this.backgroundStarDirections[index];
      const offset = direction.scale(backgroundStarDistance);
      star.position.x = this.camera.position.x + offset.x;
      star.position.y = this.camera.position.y + offset.y;
      star.position.z = this.camera.position.z + offset.z;
    });
  }

  private worldToSectorA1Position(worldX: number, worldY: number, heightOffset: number = 0): Vector3 {
    return worldToSectorA1(
      worldX,
      worldY,
      this.sectorA1Center,
      this.sectorA1Right,
      this.sectorA1Up,
      this.sectorA1Normal,
      this.COORDINATE_SCALE,
      this.WORLD_SIZE,
      heightOffset
    );
  }

  /**
   * Gets the planet surface position and normal at a world coordinate
   * Uses planet radius instead of expensive raycasting for better performance
   * @param worldX World X coordinate (0-10000)
   * @param worldY World Y coordinate (0-10000)
   * @param heightOffset Height offset from surface (default: 0)
   * @returns Object with position and normal
   */
  private getPlanetSurfacePosition(worldX: number, worldY: number, heightOffset: number = 0): { position: Vector3, normal: Vector3 } {
    if (!this.planet) {
      // Fallback if planet not available
      const position = this.worldToSectorA1Position(worldX, worldY, heightOffset);
      return { position, normal: Vector3.Up() };
    }

    const planetCentre = this.planet.position;

    // Get approximate position on sector plane
    const approxPosition = this.worldToSectorA1Position(worldX, worldY, 0);

    // Calculate direction from planet center to approximate position
    const directionToSurface = approxPosition.subtract(planetCentre).normalize();

    // Calculate surface position using planet radius (much faster than raycasting)
    const surfacePosition = planetCentre.add(directionToSurface.scale(this.planetRadius));

    // The normal is simply the direction from planet center to surface
    const surfaceNormal = directionToSurface;

    // Apply height offset along the surface normal
    const finalPosition = surfacePosition.add(surfaceNormal.scale(heightOffset));

    return { position: finalPosition, normal: surfaceNormal };
  }

  /**
   * Positions and aligns an entity to the planet surface at a world coordinate
   * Uses caching keyed by rounded world coordinates + height offset for better performance
   * Since calculation is now fast (no raycasting), we cache by grid to share between entities
   * @param mesh The mesh to position and align
   * @param worldX World X coordinate (0-10000)
   * @param worldY World Y coordinate (0-10000)
   * @param heightOffset Height offset from surface (default: 0)
   */
  private positionAndAlignToPlanetSurface(mesh: Mesh | InstancedMesh, worldX: number, worldY: number, heightOffset: number = 0): void {
    // Create cache key based on rounded world coordinates + height offset
    // This allows multiple entities at the same location to share cached data
    const roundedX = Math.round(worldX / this.SURFACE_CACHE_GRID_SIZE) * this.SURFACE_CACHE_GRID_SIZE;
    const roundedY = Math.round(worldY / this.SURFACE_CACHE_GRID_SIZE) * this.SURFACE_CACHE_GRID_SIZE;
    const cacheKey = `${roundedX}_${roundedY}_${heightOffset}`;

    let surfaceData = this.surfacePositionCache.get(cacheKey);

    // Calculate if not cached
    if (!surfaceData) {
      surfaceData = this.getPlanetSurfacePosition(roundedX, roundedY, heightOffset);
      // Cache the result for reuse by other entities at nearby positions
      this.surfacePositionCache.set(cacheKey, {
        position: surfaceData.position.clone(),
        normal: surfaceData.normal.clone()
      });
    }

    // Always use exact coordinates for positioning to ensure correct alignment
    // The cache is just to avoid recalculating the same position multiple times
    // But we need exact position/normal for proper alignment of child meshes
    const exactSurfaceData = this.getPlanetSurfacePosition(worldX, worldY, heightOffset);
    mesh.position = exactSurfaceData.position;
    this.alignEntityToPlanet(mesh, exactSurfaceData.normal);
  }

  private alignEntityToPlanet(mesh: Mesh | InstancedMesh, surfaceNormal?: Vector3): void {
    if (surfaceNormal) {
      // Align the mesh's local Y-axis (up) with the surface normal
      // We use lookAt but need to point the mesh's forward direction correctly
      // Since lookAt makes -Z point at target, we need to calculate the right target
      const target = mesh.position.add(surfaceNormal);
      // Use lookAt with proper pitch correction to align Y-axis with normal
      mesh.lookAt(target, 0, Math.PI / 2, 0);
    } else {
      // Fallback to old behavior - look at planet center
      const planetCentre = this.planet.position;
      mesh.lookAt(planetCentre, 0, Math.PI / 2, 0);
    }
  }

  private calculateSectorA1Rotation(worldDx: number, worldDy: number): number {
    const localDx = worldDx / this.COORDINATE_SCALE;
    const localDz = worldDy / this.COORDINATE_SCALE;
    return Math.atan2(localDx, localDz);
  }

  private createOrUpdateGuardLine(
    key: string,
    unitX: number,
    unitY: number,
    guardX: number,
    guardY: number,
    color: Color3
  ): void {
    let line = this.guardPositionLines[key];
    const points = [
      this.worldToSectorA1Position(unitX, unitY, 0.2),
      this.worldToSectorA1Position(guardX, guardY, 0.2)
    ];

    if (!line) {
      line = MeshBuilder.CreateLines(
        `guardLine_${key}`,
        { points, updatable: true },
        this.scene
      );
      (line as any).color = color;
      this.guardPositionLines[key] = line;
    } else {
      // Update existing line
      MeshBuilder.CreateLines(null, { points, instance: line as any, updatable: true });
      (line as any).color = color;
    }
    line.isVisible = true;
  }

  private createScene(): Scene {
    const scene = new Scene(this.engine);

    this.camera = new FreeCamera(
      "camera",
      new Vector3(0, 100, 0),
      scene
    );
    this.camera.setTarget(Vector3.Zero());

    this.camera.minZ = 0.1;
    this.camera.maxZ = 10000;

    // Adjust camera speed
    this.camera.speed = 1.0;
    this.camera.angularSensibility = 1000;

    if (this.cameraMode === 'orbit' || this.cameraMode === 'rts') {
    } else {
      this.camera.attachControl(this.canvas, true);
    }

    scene.onKeyboardObservable.add((kbInfo) => {
      switch (kbInfo.type) {
        case KeyboardEventTypes.KEYDOWN:
          switch (kbInfo.event.key.toLowerCase()) {
            case 'w':
              this.keyState.w = true;
              break;
            case 's':
              this.keyState.s = true;
              break;
            case 'a':
              this.keyState.a = true;
              break;
            case 'd':
              this.keyState.d = true;
              break;
            case 'x':
              this.keyState.x = true;
              break;
            case 'c':
              this.keyState.c = true;
              break;
            case 'm':
              // M key - toggle space layer (map view)
              this.toggleSpaceLayer();
              break;
          }
          break;
        case KeyboardEventTypes.KEYUP:
          switch (kbInfo.event.key.toLowerCase()) {
            case 'w':
              this.keyState.w = false;
              break;
            case 's':
              this.keyState.s = false;
              break;
            case 'a':
              this.keyState.a = false;
              break;
            case 'd':
              this.keyState.d = false;
              break;
            case 'x':
              this.keyState.x = false;
              break;
            case 'c':
              this.keyState.c = false;
              break;
          }
          break;
      }
    });

    // Lighting
    const light = new HemisphericLight(
      "light",
      new Vector3(0.2, 1, 0.2),
      scene
    );
    light.intensity = 1;
    light.specular = new Color3(0, 0, 0);
    light.diffuse = new Color3(1, 1, 1);
    light.groundColor = new Color3(0, 0, 0);

    // Put long rectangles from origin in the direction of x, y, and z so we can orient ourselves better
    const xAxisHelper = MeshBuilder.CreateBox("xRect", { width: 10, height: 5, depth: 0.1 }, scene);
    xAxisHelper.position = new Vector3(this.WORLD_SIZE / this.COORDINATE_SCALE / 2, 0, 0);
    const yAxisHelper = MeshBuilder.CreateBox("yRect", { width: 5, height: 10, depth: 0.1 }, scene);
    yAxisHelper.position = new Vector3(5, 5 + this.WORLD_SIZE / this.COORDINATE_SCALE / 2, 0);
    const zAxisHelper = MeshBuilder.CreateBox("zRect", { width: 0.1, height: 5, depth: 10 }, scene);
    zAxisHelper.position = new Vector3(0, 0, this.WORLD_SIZE / this.COORDINATE_SCALE / 2);
    const xRectMaterial = new StandardMaterial("xRectMaterial", scene);
    xRectMaterial.diffuseColor = new Color3(1, 0, 0);
    xAxisHelper.material = xRectMaterial;
    const yRectMaterial = new StandardMaterial("yRectMaterial", scene);
    yRectMaterial.diffuseColor = new Color3(0, 1, 0);
    yAxisHelper.material = yRectMaterial;
    const zRectMaterial = new StandardMaterial("zRectMaterial", scene);
    zRectMaterial.diffuseColor = new Color3(0, 0, 1);
    zAxisHelper.material = zRectMaterial;

    const halfWidth = (this.WORLD_SIZE / this.COORDINATE_SCALE) / 2;
    const fullWidth = this.WORLD_SIZE / this.COORDINATE_SCALE;

    const hsvToRgb = (h: number, s: number, v: number) => {
      return Color3.FromHSV(h, s, v);
    }

    const satelliteMesh = MeshBuilder.CreateGround(
      "satelliteMesh",
      { width: 30, height: 30 },
      scene
    );
    const satelliteMaterial = new StandardMaterial("satelliteMaterial", scene);
    satelliteMaterial.diffuseColor = new Color3(0.1, 0.8, 0.8);
    satelliteMaterial.backFaceCulling = false;
    satelliteMaterial.alpha = 0.5;
    satelliteMesh.material = satelliteMaterial;
    satelliteMesh.position = new Vector3(halfWidth, 0, halfWidth + this.WORLD_SIZE / this.COORDINATE_SCALE + 50);

    // Add a giant sphere far below us, i.e., we're on a planet
    // Tile "most" of the sphere with squares (arenas) of the same size
    // Strategy: Use latitude bands (rings at different Y offsets), excluding polar regions

    const ARENA_SIZE = 10000; // Game coordinates (10000x10000)
    const ARENA_SIZE_VISUAL = ARENA_SIZE / this.COORDINATE_SCALE; // Visual size (200x200)

    // Planet center and dimensions
    const PLANET_CENTER_X = halfWidth;
    const PLANET_CENTER_Y = -500;
    const PLANET_CENTER_Z = halfWidth;

    const TARGET_SQUARES_PER_EQUATOR = 16;
    const EQUATOR_CIRCUMFERENCE = TARGET_SQUARES_PER_EQUATOR * ARENA_SIZE_VISUAL;
    const REQUIRED_EQUATOR_RADIUS = EQUATOR_CIRCUMFERENCE / (2 * Math.PI);
    let OPTIMAL_PLANET_DIAMETER = REQUIRED_EQUATOR_RADIUS * 2;

    // Let's just make it a power of two for now, humans like that...
    OPTIMAL_PLANET_DIAMETER = 1024;

    const PLANET_DIAMETER = Math.round(OPTIMAL_PLANET_DIAMETER);
    const PLANET_RADIUS = PLANET_DIAMETER / 2;
    this.planetRadius = PLANET_RADIUS; // Store for surface position calculations

    // Polar exclusion: don't place squares too close to the poles
    const POLAR_EXCLUSION_ANGLE = Math.PI / 5;
    const MAX_Y_OFFSET = PLANET_RADIUS * Math.cos(POLAR_EXCLUSION_ANGLE);
    const MIN_BAND_RADIUS = ARENA_SIZE_VISUAL * 0.5; // Don't place bands where radius is too small

    const planet = MeshBuilder.CreateSphere("planet", { diameter: PLANET_DIAMETER }, scene);
    this.planet = planet;
    planet.position = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);
    const planetMaterial = new StandardMaterial("planetMaterial", scene);
    planetMaterial.diffuseColor = new Color3(0.5, 0.5, 0.5);
    planetMaterial.alpha = 0;
    planet.material = planetMaterial;

    // Create low - poly terrain
    const terrain = createLowPolyTerrain(
      scene,
      new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z),
      PLANET_RADIUS
    );
    this.terrainMesh = terrain;
    // Make terrain pickable for raycasting
    terrain.isPickable = true;

    // Create sectors/arenas in latitude bands
    const sectorMaterial = new StandardMaterial("sectorMaterial", scene);
    sectorMaterial.diffuseColor = new Color3(0.2, 0.6, 0.8);
    sectorMaterial.alpha = 0.3;
    sectorMaterial.emissiveColor = new Color3(0.1, 0.3, 0.4);

    const outlineMaterial = new StandardMaterial("sectorOutlineMaterial", scene);
    outlineMaterial.diffuseColor = new Color3(0.3, 0.7, 1);
    outlineMaterial.wireframe = true;
    outlineMaterial.alpha = 0.3;
    outlineMaterial.emissiveColor = new Color3(0.2, 0.5, 0.7);

    // Calculate latitude bands
    // Each band is a circle in the X-Z plane at constant Y
    // At Y offset dy from center, the circle radius is: sqrt(R^2 - dy^2)
    const Y_STEP = ARENA_SIZE_VISUAL; // Step by one arena size
    const bands: Array<{ y: number, radius: number, count: number }> = [];


    // Start from negative Z and work our way up
    // for (let yOffset = MAX_Y_OFFSET; yOffset >= -MAX_Y_OFFSET; yOffset -= Y_STEP) {
    //   // Calculate the radius of the circle at this Y offset
    //   const bandRadius = Math.sqrt(PLANET_RADIUS * PLANET_RADIUS - yOffset * yOffset);

    //   // Skip if radius is too small
    //   if (bandRadius < MIN_BAND_RADIUS) continue;

    //   // Calculate how many squares fit around this band
    //   const circumference = 2 * Math.PI * bandRadius;
    //   const squaresInBand = Math.floor(circumference / ARENA_SIZE_VISUAL);

    //   if (squaresInBand < 1) continue; // Skip if we can't fit at least one

    //   bands.push({ y: yOffset, radius: bandRadius, count: squaresInBand });
    // }


    const desired = [8, 12, 16, 12, 8];
    for (let i = 0; i < desired.length; i++) {
      const yOffset = MAX_Y_OFFSET - i * Y_STEP;
      const bandRadius = Math.sqrt(PLANET_RADIUS * PLANET_RADIUS - yOffset * yOffset);
      const count = desired[i];
      bands.push({ y: yOffset, radius: bandRadius, count: count });
    }

    let totalSectors = bands.reduce((sum, band) => sum + band.count, 0);

    // Create sectors for each band
    let sectorIndex = 0;
    bands.forEach((band, bandIndex) => {
      const y = PLANET_CENTER_Y + band.y;
      const angleStep = (2 * Math.PI) / band.count;

      for (let i = 0; i < band.count; i++) {
        const angle = i * angleStep;
        const helperAngle = angle;

        // Position on the circle in X-Y plane at this Z
        const centerX = PLANET_CENTER_X + Math.cos(angle) * band.radius;
        const centerY = y;
        const centerZ = PLANET_CENTER_Z + Math.sin(angle) * band.radius;

        const helperX = PLANET_CENTER_X + Math.cos(helperAngle) * band.radius;
        const helperY = y;
        const helperZ = PLANET_CENTER_Z + Math.sin(helperAngle) * band.radius;

        // Create sector box
        const sector = MeshBuilder.CreateBox(
          `futureSector_${sectorIndex}`,
          {
            width: ARENA_SIZE_VISUAL * 0.8,
            height: 0.1,
            depth: ARENA_SIZE_VISUAL * 0.8
          },
          scene
        );

        const planetCentre = new Vector3(PLANET_CENTER_X, PLANET_CENTER_Y, PLANET_CENTER_Z);
        const sectorCentre = new Vector3(centerX, centerY, centerZ);
        const helperCentre = new Vector3(helperX, helperY, helperZ);
        sector.position = new Vector3(centerX, centerY, centerZ);
        sector.lookAt(planetCentre, 0, Math.PI / 2, 0);

        // Determine sector color based on band and position
        let sectorColor: Color3;
        let sectorAlpha: number;
        let emissiveColor: Color3;

        const activeSectorColour = new Color3(0.2, 0.6, 0.8);
        const activeSectorEmissiveColour = new Color3(0.1, 0.3, 0.4);
        const fadedSectorColour = new Color3(0.1, 0.1, 0.15);
        const fadedSectorEmissiveColour = new Color3(0.1, 0.1, 0.15);
        const darkGreySectorColour = new Color3(0.1, 0.1, 0.1);
        const darkGreySectorEmissiveColour = new Color3(0.1, 0.1, 0.1);
        const darkOrangeSectorColour = new Color3(0.6, 0.1, 0.1);
        const darkOrangeSectorEmissiveColour = new Color3(0.1, 0.1, 0.1);

        if (bandIndex === 0 && i === 0) {
          sectorColor = activeSectorColour;
          emissiveColor = activeSectorEmissiveColour;
          sectorAlpha = 0;
        } else if (bandIndex === 0) {
          sectorColor = fadedSectorColour;
          emissiveColor = fadedSectorEmissiveColour;
          sectorAlpha = 0.8;
        } else if (bandIndex === 1) {
          sectorColor = darkGreySectorColour;
          emissiveColor = darkGreySectorEmissiveColour;
          sectorAlpha = 0.8;
        } else if (bandIndex === 2 || bandIndex === 3) {
          // C and D band sectors - dark orange with variation
          // Base dark orange: RGB around (0.6, 0.3, 0.1)
          // Variation based on modulo 2 and modulo 3 for mysterious but consistent coloring
          const mod2 = i % 2;
          const mod3 = i % 3;
          // Create subtle variation: combine mod2 and mod3 for 6 different shades
          const shadeVariation = (mod2 * 0.05 + mod3 * 0.03);
          const r = 0.6 - shadeVariation; // Range: 0.52 to 0.6
          const g = 0.1 - shadeVariation * 0.5; // Range: 0.27 to 0.3
          const b = 0.1 - shadeVariation * 0.3; // Range: 0.08 to 0.1
          sectorColor = new Color3(
            Math.max(0.7, Math.min(0.65, r)),
            Math.max(0.25, Math.min(0.32, g)),
            Math.max(0.08, Math.min(0.12, b))
          );
          emissiveColor = darkOrangeSectorEmissiveColour;
          sectorAlpha = 0.8;
        } else {
          // E band or other bands - use default
          sectorColor = darkOrangeSectorColour;
          emissiveColor = darkOrangeSectorEmissiveColour;
          sectorAlpha = 0.8;
        }

        // Create material for this sector
        const material = new StandardMaterial(`sectorMaterial_${bandIndex}_${i}`, scene);
        material.diffuseColor = sectorColor;
        material.alpha = sectorAlpha;
        material.emissiveColor = emissiveColor;
        sector.material = material;

        if (bandIndex === 0 && i === 0) {
        } else {
          const sectorOutline = MeshBuilder.CreateBox(
            `futureSectorOutline_${sectorIndex}`,
            {
              width: ARENA_SIZE_VISUAL * 0.8,
              height: 0.1,
              depth: ARENA_SIZE_VISUAL * 0.8
            },
            scene
          );

          sectorOutline.position = sectorCentre;
          sectorOutline.lookAt(planetCentre, 0, Math.PI / 2, 0);
          sectorOutline.material = outlineMaterial;
        }
        // Add our traditional x, y, z, helpers to the sector
        const xAxisHelperClone = xAxisHelper.clone("xAxisHelperClone_" + sectorIndex);
        xAxisHelperClone.position = helperCentre;
        xAxisHelperClone.material = xRectMaterial;
        const yAxisHelperClone = yAxisHelper.clone("yAxisHelperClone_" + sectorIndex);
        yAxisHelperClone.position = helperCentre;
        yAxisHelperClone.material = yRectMaterial;
        const zAxisHelperClone = zAxisHelper.clone("zAxisHelperClone_" + sectorIndex);
        zAxisHelperClone.position = helperCentre;
        zAxisHelperClone.material = zRectMaterial;

        const dx = planetCentre.x - helperCentre.x;
        const dy = planetCentre.y - helperCentre.y;
        const dz = planetCentre.z - helperCentre.z;

        // And also derive those three angles from the sector centre to the planet centre

        // Note this... careful!
        const pitchCorrection = Math.PI / 2;

        const yaw = -Math.atan2(dz, dx) + Math.PI / 2;
        const len = Math.sqrt(dx * dx + dz * dz);
        const pitch = -Math.atan2(dy, len) + pitchCorrection;

        const overallRotation = new Vector3(pitch, yaw, 0);

        const centerVector = new Vector3(dx, dy, dz);

        const existingRotation = centerVector.clone().normalize();
        let upIsh = new Vector3(0, 1, 0);
        if (Math.abs(upIsh.dot(existingRotation)) > 0.99) {
          upIsh = new Vector3(0, 0, 1);
        }
        const right = upIsh.cross(existingRotation).normalize();
        const up = existingRotation.cross(right).normalize();
        const newXPosition = helperCentre.add(up.scale(-80)).add(right.scale(-75));
        const newYPosition = helperCentre.add(existingRotation.scale(-7.5)).add(right.scale(-77.5)).add(up.scale(-80));
        const newZPosition = helperCentre.add(right.scale(-80)).add(up.scale(-75));
        xAxisHelperClone.position = newXPosition;
        yAxisHelperClone.position = newYPosition;
        zAxisHelperClone.position = newZPosition;
        xAxisHelperClone.rotation = overallRotation;
        yAxisHelperClone.rotation = overallRotation;
        zAxisHelperClone.rotation = overallRotation;

        // Store sector A1 coordinate system (bandIndex=0, i=0)
        if (bandIndex === 0 && i === 0) {
          this.sectorA1Center = sectorCentre.clone();
          this.sectorA1Normal = existingRotation.clone(); // Points toward planet center
          this.sectorA1Right = right.clone();
          this.sectorA1Up = up.clone();
        }

        // Create sector label
        const labelPosition = new Vector3(centerX, centerY, centerZ).add(existingRotation.scale(-200));
        const bandLetter = String.fromCharCode(65 + bandIndex); // A, B, C, D, E
        const sectorNumber = i + 1; // 1, 2, 3...
        const labelText = `${bandLetter}${sectorNumber}`;
        this.createSectorLabel(`sector_${bandIndex}_${i}`, labelPosition.x, labelPosition.y, labelPosition.z, labelText);

        sectorIndex++;
      }
    });


    xAxisHelper.dispose();
    yAxisHelper.dispose();
    zAxisHelper.dispose();

    // Add the orbital station
    const orbitalStation = MeshBuilder.CreateSphere("orbitalStation", { diameter: 100 }, scene);
    orbitalStation.position = new Vector3(200, 500, 200);
    const orbitalStationMaterial = new StandardMaterial("orbitalStationMaterial", scene);
    orbitalStationMaterial.diffuseColor = new Color3(0.5, 0.5, 0.5);
    orbitalStation.material = orbitalStationMaterial;

    // Add space ships (big rectangles) in the space layer
    const SPACE_LAYER_HEIGHT = 1000;
    const shipMaterial = new StandardMaterial("shipMaterial", scene);
    shipMaterial.diffuseColor = new Color3(0.3, 0.3, 0.7);
    shipMaterial.emissiveColor = new Color3(0.1, 0.1, 0.3);

    // Planet center (in scaled coordinates)
    const planetCenterX = halfWidth;
    const planetCenterY = -500;
    const planetCenterZ = halfWidth;

    // Create a few ships with orbital mechanics
    // Each ship will orbit at different radii and speeds
    const shipConfigs = [
      { radius: 900, orbitalSpeed: 0.00005, initialAngle: 0 },
      { radius: 950, orbitalSpeed: 0.00004, initialAngle: Math.PI / 3 },
      { radius: 1000, orbitalSpeed: 0.00003, initialAngle: Math.PI * 2 / 3 },
      { radius: 1050, orbitalSpeed: 0.000035, initialAngle: Math.PI },
      { radius: 1100, orbitalSpeed: 0.000025, initialAngle: Math.PI * 4 / 3 }
    ];

    shipConfigs.forEach((config, index) => {
      const ship = MeshBuilder.CreateBox(
        `spaceShip_${index}`,
        { width: 80, height: 20, depth: 120 },
        scene
      );

      // Calculate initial position based on orbital angle
      const initialX = planetCenterX + Math.cos(config.initialAngle) * config.radius;
      const initialZ = planetCenterZ + Math.sin(config.initialAngle) * config.radius;

      ship.position = new Vector3(initialX, SPACE_LAYER_HEIGHT, initialZ);
      ship.material = shipMaterial;

      // Store orbital data
      this.spaceShips.push({
        mesh: ship,
        angle: config.initialAngle,
        radius: config.radius,
        orbitalSpeed: config.orbitalSpeed,
        centerX: planetCenterX,
        centerY: planetCenterY,
        centerZ: planetCenterZ
      });
    });

    // Add the star
    const star = MeshBuilder.CreateSphere("star", { diameter: 100 }, scene);
    star.position = new Vector3(500, 500, 500);
    const starMaterial = new StandardMaterial("starMaterial", scene);
    star.material = starMaterial;
    starMaterial.emissiveColor = new Color3(0.6, 0.6, 1);
    starMaterial.alpha = 0.1;
    starMaterial.disableLighting = true;
    this.star = star;

    // Add a few more stars with slight offsets so that the alpha stacks to make a nice glow
    const starCount = 9;
    for (let i = 0; i < starCount; i++) {
      const star = MeshBuilder.CreateSphere("star_" + i, { diameter: 100 + i * 3 }, scene);
      star.position = new Vector3(500, 500, 500);
      star.material = starMaterial;
      this.stars.push(star);
    }

    // Add background stars that are always visible but unreachable;
    const backgroundStarCount = 50;
    const backgroundStarDistance = 1000;
    const backgroundStarMaterial = new StandardMaterial("backgroundStarMaterial", scene);
    backgroundStarMaterial.emissiveColor = new Color3(0.8, 0.8, 1);
    backgroundStarMaterial.alpha = 0.8;
    backgroundStarMaterial.disableLighting = true;

    let seed = 54321;
    const seededRandom = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const seededRandomFloat = (min: number, max: number) => min + (max - min) * seededRandom();

    for (let i = 0; i < backgroundStarCount; i++) {
      const theta = seededRandomFloat(0, Math.PI * 2);
      const phi = Math.acos(seededRandomFloat(-1, 1));
      const direction = new Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.sin(phi) * Math.sin(theta),
        Math.cos(phi)
      );

      const starSize = seededRandomFloat(3, 10);
      const backgroundStar = MeshBuilder.CreateSphere("backgroundStar_" + i, { diameter: starSize }, scene);
      backgroundStar.position = direction.scale(backgroundStarDistance);
      backgroundStar.material = backgroundStarMaterial;
      this.backgroundStars.push(backgroundStar);
      this.backgroundStarDirections.push(direction.clone());
    }

    let angle = 0;
    const origin = planet.position;
    const radius = 900;
    const detail = 20;
    for (let i = 0; i < detail; i++) {
      angle = i * 2 * Math.PI / detail;
      const satelliteClone = satelliteMesh.clone("satelliteClone_" + i);
      satelliteClone.material = satelliteMaterial;
      satelliteClone.position = new Vector3(origin.x + Math.cos(angle) * radius, origin.y + Math.sin(angle) * radius - 5, halfWidth);
      satelliteClone.rotation = new Vector3(0, 0, angle - Math.PI / 2);
      this.satellites.push(satelliteClone);
    }

    // Initialize camera based on mode
    if (this.cameraMode === 'orbit') {
      this.camera.detachControl();
      this.initializeOrbitCamera();
    } else if (this.cameraMode === 'rts') {
      this.camera.detachControl();
      this.initializeRTSCamera();
    } else {
      this.camera.attachControl(this.canvas, true);
      this.camera.setTarget(new Vector3(halfWidth, 0, halfWidth));
    }

    return scene;
  }

  /**
   * Start quadtree visualization updates
   */
  private startQuadtreeVisualization(): void {
    this.updateQuadtreeVisualization();
    this.quadtreeUpdateInterval = window.setInterval(() => {
      this.updateQuadtreeVisualization();
    }, 10000);
  }

  /**
   * Fetch quadtree data from server and update visualization
   */
  private async updateQuadtreeVisualization(): Promise<void> {
    try {
      const response = await fetch('/api/quadtrees');
      if (!response.ok) {
        console.warn('Failed to fetch quadtree data');
        return;
      }
      const quadtrees = await response.json();
      this.renderQuadtrees(quadtrees);
    } catch (error) {
      console.warn('Error fetching quadtree data:', error);
    }
  }

  /**
   * Render quadtrees as outlined rectangles with different colors and Y offsets
   */
  private renderQuadtrees(quadtrees: {
    units: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
    buildings: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
    ores: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>;
  }): void {
    this.clearQuadtreeMeshes();

    // Only render if toggles are enabled
    if (!this.showQuadtreeUnits && !this.showQuadtreeBuildings && !this.showQuadtreeOres) {
      return;
    }

    const colors = {
      units: new Color3(1, 0, 0),
      buildings: new Color3(0, 1, 0),
      ores: new Color3(0, 0, 1)
    };

    const yOffsets = {
      units: 0.5,
      buildings: 1.0,
      ores: 1.5
    };

    if (this.showQuadtreeUnits) {
      this.renderQuadtreeType('units', quadtrees.units, colors.units, yOffsets.units);
    }
    if (this.showQuadtreeBuildings) {
      this.renderQuadtreeType('buildings', quadtrees.buildings, colors.buildings, yOffsets.buildings);
    }
    if (this.showQuadtreeOres) {
      this.renderQuadtreeType('ores', quadtrees.ores, colors.ores, yOffsets.ores);
    }
  }

  /**
   * Render a specific quadtree type
   */
  private renderQuadtreeType(
    type: 'units' | 'buildings' | 'ores',
    nodes: Array<{ x: number, y: number, width: number, height: number, depth: number, entityCount: number }>,
    color: Color3,
    yOffset: number
  ): void {
    // Check if sector A1 coordinate system is available
    if (!this.sectorA1Center || !this.sectorA1Right || !this.sectorA1Up || !this.sectorA1Normal) {
      return;
    }

    nodes.forEach((node, index) => {
      // Calculate rectangle corners in world coordinates
      const worldX1 = node.x;
      const worldY1 = node.y;
      const worldX2 = node.x + node.width;
      const worldY2 = node.y;
      const worldX3 = node.x + node.width;
      const worldY3 = node.y + node.height;
      const worldX4 = node.x;
      const worldY4 = node.y + node.height;

      // Transform each corner to sector A1 coordinate system
      const p1 = worldToSectorA1(
        worldX1, worldY1,
        this.sectorA1Center,
        this.sectorA1Right,
        this.sectorA1Up,
        this.sectorA1Normal,
        this.COORDINATE_SCALE,
        this.WORLD_SIZE,
        yOffset
      );
      const p2 = worldToSectorA1(
        worldX2, worldY2,
        this.sectorA1Center,
        this.sectorA1Right,
        this.sectorA1Up,
        this.sectorA1Normal,
        this.COORDINATE_SCALE,
        this.WORLD_SIZE,
        yOffset
      );
      const p3 = worldToSectorA1(
        worldX3, worldY3,
        this.sectorA1Center,
        this.sectorA1Right,
        this.sectorA1Up,
        this.sectorA1Normal,
        this.COORDINATE_SCALE,
        this.WORLD_SIZE,
        yOffset
      );
      const p4 = worldToSectorA1(
        worldX4, worldY4,
        this.sectorA1Center,
        this.sectorA1Right,
        this.sectorA1Up,
        this.sectorA1Normal,
        this.COORDINATE_SCALE,
        this.WORLD_SIZE,
        yOffset
      );

      const points = [
        p1,
        p2,
        p3,
        p4,
        p1 // Close the rectangle
      ];

      const line = MeshBuilder.CreateLines(
        `quadtree_${type}_${index}`,
        { points: points },
        this.scene
      );

      line.color = color;

      this.quadtreeMeshes[type].push(line);
    });
  }

  /**
   * Clear all quadtree visualization meshes
   */
  private clearQuadtreeMeshes(): void {
    Object.keys(this.quadtreeMeshes).forEach(type => {
      this.quadtreeMeshes[type as keyof typeof this.quadtreeMeshes].forEach(mesh => {
        mesh.dispose();
      });
      this.quadtreeMeshes[type as keyof typeof this.quadtreeMeshes] = [];
    });
  }

  private saveCode(): void {
    const file = document.getElementById('file') as HTMLInputElement;
    file.click();
  }

  private saveCodeToServer(code: string): void {
    fetch('/api/code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        playerId: this.worldPlayerId
      })
    }).then(() => {
      console.log('Code saved');
      const file = document.getElementById('file') as HTMLInputElement;
      file.value = '';
    }).catch(() => {
      console.error('Failed to save code');
    });
  }

  private hasNoHubBuildingsOrUnits(): boolean {
    if (this.worldPlayerId < 0) {
      return false;
    }
    const myBuildings = this.serverBuildings[this.worldPlayerId] || [];
    const myUnits = this.serverUnits[this.worldPlayerId] || [];

    const hasHub = myBuildings.some(b => b.k === BuildingType.Hub);

    return !hasHub && myBuildings.length === 0 && myUnits.length === 0;
  }

  private updateRespawnButtonState(): void {
    const respawnButton = document.getElementById('respawn') as HTMLButtonElement;
    if (!respawnButton || this.worldPlayerId < 0) {
      return;
    }

    const canRespawn = this.hasNoHubBuildingsOrUnits();
    respawnButton.disabled = !canRespawn;

    if (canRespawn) {
      respawnButton.style.backgroundColor = '#0891b2';
      respawnButton.style.opacity = '1';
      respawnButton.style.cursor = 'pointer';
    } else {
      respawnButton.style.backgroundColor = '#475569';
      respawnButton.style.opacity = '0.6';
      respawnButton.style.cursor = 'not-allowed';
    }
  }

  private handleRespawn(): void {
    if (!this.hasNoHubBuildingsOrUnits()) {
      console.log('Cannot respawn: you have buildings or units');
      return;
    }

    let code = '';
    const aceEditor = (window as any).editor;
    if (aceEditor) {
      code = aceEditor.getValue() || '';
    }

    if (!code) {
      const savedCode = localStorage.getItem('overwhelmed-code');
      if (savedCode) {
        code = savedCode;
      }
    }

    this.saveCodeToServer(code);
  }

  /**
   * Queue a console message to be processed in the render loop
   * This batches console updates to align with the game's update cycle
   */
  public queueConsoleMessage(message: string, color?: string): void {
    this.consoleMessageQueue.push({ message, color });
  }

  /**
   * Process console message queue in the render loop
   * Batches multiple messages together for better performance
   */
  private updateConsole(): void {
    if (this.consoleMessageQueue.length === 0 && !this.consoleNeedsScroll) {
      return;
    }

    const gameConsole = document.getElementById('console');
    if (!gameConsole) return;

    // Check if user is near the bottom (within 50px) - if so, auto-scroll
    const isNearBottom = gameConsole.scrollHeight - gameConsole.scrollTop - gameConsole.clientHeight < 50;

    // Process all queued messages at once
    if (this.consoleMessageQueue.length > 0) {
      const fragment = document.createDocumentFragment();
      this.consoleMessageQueue.forEach(({ message, color }) => {
        const p = document.createElement('p');
        if (color) {
          p.style.color = color;
        }
        p.textContent = message;
        fragment.appendChild(p);
      });

      gameConsole.appendChild(fragment);
      this.consoleMessageQueue = [];

      // If height overflow, remove oldest messages
      while (gameConsole.scrollHeight > 10 * gameConsole.clientHeight) {
        const firstChild = gameConsole.firstChild;
        if (firstChild) {
          gameConsole.removeChild(firstChild);
        } else {
          break;
        }
      }

      // Mark that we need to scroll if user was near bottom
      if (isNearBottom) {
        this.consoleNeedsScroll = true;
      }
    }

    // Perform scroll if needed (after DOM updates)
    if (this.consoleNeedsScroll) {
      gameConsole.scrollTop = gameConsole.scrollHeight;
      this.consoleNeedsScroll = false;
    }
  }
}

// Start the game when the window loads
window.addEventListener('DOMContentLoaded', () => {
  const game = new Game3D();
  window.gameInstance = game;
}); 